import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import "./LandingPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Tooltip } from "bootstrap";
import navbarPlaceholder from "../assets/navbar-placeholder.png";
import workHoursMockup from "../assets/workhours-mockup.png";
import workHoursMockupDark from "../assets/workhours-mockup-dark.png";
import evalMockupDark from "../assets/evaluation-mockup-dark.png";
import evalMockup from "../assets/evaluation-mockup.png";
import conferenceMockupDark from "../assets/conference-mockup-dark.png";
import conferenceMockup from "../assets/conference-mockup.png";
import demoCapture0000 from "../assets/demo-mode_0000.png";
import demoCapture0001 from "../assets/demo-mode_0001.png";
import demoCapture0002 from "../assets/demo-mode_0002.png";
import demoCapture0003 from "../assets/demo-mode_0003.png";
import demoCapture0004 from "../assets/demo-mode_0004.png";
import demoCapture0005 from "../assets/demo-mode_0005.png";
import demoCapture0006 from "../assets/demo-mode_0006.png";
import demoCapture0007 from "../assets/demo-mode_0007.png";
import demoCapture0008 from "../assets/demo-mode_0008.png";
import demoCapture0009 from "../assets/demo-mode_0009.png";
import demoCapture0010 from "../assets/demo-mode_0010.png";
import demoCapture0011 from "../assets/demo-mode_0011.png";
import demoCapture0012 from "../assets/demo-mode_0012.png";
import demoCapture0013 from "../assets/demo-mode_0013.png";
import demoCapture0014 from "../assets/demo-mode_0014.png";
import demoCapture0015 from "../assets/demo-mode_0015.png";
// Import additional mockup images (you can add more images to assets folder)
// import resDashboard2 from '../assets/res_dashboard2.jpg';
// import resDashboard3 from '../assets/res_dashboard3.jpg';
import { API_ENDPOINTS } from "../config/api";
import { SPECIALTY_ROTATION_TYPES } from "../utils/rotationTypesConfig";
import { Row, Col, Nav, Tab, Accordion, FloatingLabel, Form } from "react-bootstrap";

const FEATURE_PHRASES = [
  "rotation scheduling",
  "document onboarding",
  "conference scheduling",
  "evaluations",
  "work hour tracking",
  "case logging",
  "alerts & notifications",

];

const HOW_IT_WORKS_ACCOUNT_CARD_TITLE = "Create your account";

const HOW_IT_WORKS_SETUP_ITEMS = [
  "Set up your program",
  "Add residents",
  "Create rotations",
  "Evaluations",
  "Checklist items",
  "Onboarding documents",
];

const HOW_IT_WORKS_ASSIGN_ITEMS = [
  "Assign residents to rotations and blocks",
  "Staff call, clinic, elective, and float coverage",
  "Publish cohort schedules when ready",
  "Share calendars and one-click subscriptions (iCal)",
];

const HOW_IT_WORKS_TRACK_ITEMS = [
  "Monitor evaluations and milestone progress",
  "Track conference attendance and learning resources",
  "Review work-hour compliance and fatigue risk",
  "Run dashboards for onboarding and program ops",
];

const HOW_IT_WORKS_ASSIGN_START = HOW_IT_WORKS_SETUP_ITEMS.length;
const HOW_IT_WORKS_TRACK_START =
  HOW_IT_WORKS_ASSIGN_START + HOW_IT_WORKS_ASSIGN_ITEMS.length;

/** Checklist rows inside the three columns (solo account precedes these) */
const HOW_IT_WORKS_GRID_STEP_COUNT =
  HOW_IT_WORKS_SETUP_ITEMS.length +
  HOW_IT_WORKS_ASSIGN_ITEMS.length +
  HOW_IT_WORKS_TRACK_ITEMS.length;

const HOW_IT_WORKS_SOLO_TICKS = 2;
/** Closing solo card after grid (celebrate → peace-of-mind emphasis) */
const HOW_IT_WORKS_CLOSING_SOLO_TICKS = 2;

const HOW_IT_WORKS_GRID_START_SEQ = HOW_IT_WORKS_SOLO_TICKS;
const HOW_IT_WORKS_SUCCESS_START_SEQ =
  HOW_IT_WORKS_GRID_START_SEQ + HOW_IT_WORKS_GRID_STEP_COUNT;

const HOW_IT_WORKS_FULL_SEQUENCE_LEN =
  HOW_IT_WORKS_SUCCESS_START_SEQ + HOW_IT_WORKS_CLOSING_SOLO_TICKS;

const HOW_IT_WORKS_CLOSING_TITLE = "Success & peace of mind";
const HOW_IT_WORKS_CLOSING_CAPTIONS = [
  "Your workflows are synced—scheduling, tracking, education, and compliance in one hive.",
  "Step back knowing your program stays visible, actionable, and ready for survey season.",
];

const HOW_IT_WORKS_SEQ_MS = 1650;

const PROBLEM_SOLUTION_PROBLEMS = [
  {
    title: "Spreadsheets for scheduling",
    detail:
      "Rotation changes live in scattered files, emails, and inbox threads—making updates difficult to track and easy to miss.",
  },
  {
    title: "Fragmented apps and logins",
    detail:
      "Scheduling, evaluations, duty hours, and education live across separate platforms, each with its own workflow, login, and duplicate data entry.",
  },
  {
    title: "Communication gaps",
    detail:
      "Important updates disappear across texts, emails, and chat threads, leaving residents and staff out of sync.",
  },
  {
    title: "Sign-in sheets and manual education tracking",
    detail:
      "Conference attendance, sign-ins, and compliance records are often reconstructed by hand when accreditation deadlines approach.",
  },
];

const PROBLEM_SOLUTION_SOLUTIONS = [
  {
    title: "Smart automated scheduling",
    detail:
      "Replace one-off spreadsheets with live schedules, built-in guardrails, and centralized updates everyone sees in real time.",
  },
  {
    title: "One workspace instead of tool sprawl",
    detail:
      "Scheduling, evaluations, work hours, conferences, and communication all live in one system—without duplicate accounts or disconnected workflows.",
  },
  {
    title: "Centralized communication",
    detail:
      "Keep announcements, schedule changes, and educational updates tied directly to the people, rotations, and events they affect.",
  },
  {
    title: "Conference and education you can prove",
    detail:
      "Attendance, evaluations, and program activity become part of a connected system of record—organized, searchable, and ready when you need it.",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [currentSpecialtyIndex, setCurrentSpecialtyIndex] = useState(0);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [typedFeatureText, setTypedFeatureText] = useState("");
  const [isFeaturesVisible, setIsFeaturesVisible] = useState(true);
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    company: "",
    dateTime: "",
    message: "",
  });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [demoError, setDemoError] = useState("");
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [imageOpacity, setImageOpacity] = useState(1);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const hierarchyChartRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return false; // default to light for landing page
  });
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  const [howItWorksSeq, setHowItWorksSeq] = useState(0);
  const [howItWorksMotionOk, setHowItWorksMotionOk] = useState(true);
  /** User clicked a segment (or keyed); autoplay paused until resumed */
  const [howItWorksAutoplayPaused, setHowItWorksAutoplayPaused] =
    useState(false);
  const [activeRole, setActiveRole] = useState(null);
  const activeRoleRef = useRef(null);
  const orbitControlRef = useRef({ pause: () => {}, resume: () => {} });

  // Storylane demo loader and launcher
  const storylaneScriptUrl = "https://js.storylane.io/js/v2/storylane.js";
  const playStorylaneDemo = (event) => {
    event.preventDefault();
    const invokePlay = () => {
      if (window.Storylane && typeof window.Storylane.Play === "function") {
        window.Storylane.Play({
          type: "popup",
          demo_type: "image",
          width: 1417,
          height: 987,
          scale: "0.95",
          demo_url: "https://app.storylane.io/demo/ctjpopwrmk9a?embed=popup",
          padding_bottom: "calc(69.65% + 25px)",
        });
      }
    };

    if (window.Storylane) {
      invokePlay();
      return;
    }

    const existing = document.querySelector(
      `script[src="${storylaneScriptUrl}"]`,
    );
    if (existing) {
      existing.addEventListener("load", invokePlay, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = storylaneScriptUrl;
    script.async = true;
    script.addEventListener("load", invokePlay, { once: true });
    document.body.appendChild(script);
  };

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const token = localStorage.getItem("token");
    const isLikelyJwt = !!token && token.split(".").length === 3;

    if (token && isLikelyJwt) {
      // User appears to be logged in, redirect to dashboard
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setHowItWorksMotionOk(!mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!howItWorksMotionOk || howItWorksAutoplayPaused) return undefined;
    const id = window.setInterval(() => {
      setHowItWorksSeq((i) => (i + 1) % HOW_IT_WORKS_FULL_SEQUENCE_LEN);
    }, HOW_IT_WORKS_SEQ_MS);
    return () => clearInterval(id);
  }, [howItWorksMotionOk, howItWorksAutoplayPaused]);

  const goHowItWorksStep = useCallback((stepIndex) => {
    const max = HOW_IT_WORKS_FULL_SEQUENCE_LEN - 1;
    const clamped = Math.max(0, Math.min(max, stepIndex));
    setHowItWorksSeq(clamped);
    if (howItWorksMotionOk) setHowItWorksAutoplayPaused(true);
  }, [howItWorksMotionOk]);

  const resumeHowItWorksAutoplay = useCallback(() => {
    setHowItWorksAutoplayPaused(false);
  }, []);

  const onHowWorksSegmentKeyDown = useCallback(
    (e, slotIndex) => {
      const maxIdx = HOW_IT_WORKS_FULL_SEQUENCE_LEN - 1;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goHowItWorksStep(slotIndex + 1);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goHowItWorksStep(slotIndex - 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        goHowItWorksStep(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goHowItWorksStep(maxIdx);
      }
    },
    [goHowItWorksStep],
  );

  const howItWorksShowSoloStage =
    howItWorksSeq < HOW_IT_WORKS_SOLO_TICKS;
  const howItWorksShowClosingStage =
    howItWorksSeq >= HOW_IT_WORKS_SUCCESS_START_SEQ &&
    howItWorksSeq < HOW_IT_WORKS_FULL_SEQUENCE_LEN;
  const howItWorksShowGrid =
    howItWorksSeq >= HOW_IT_WORKS_GRID_START_SEQ &&
    howItWorksSeq < HOW_IT_WORKS_SUCCESS_START_SEQ;

  const howItWorksGridIx =
    howItWorksSeq >= HOW_IT_WORKS_GRID_START_SEQ &&
    howItWorksSeq < HOW_IT_WORKS_SUCCESS_START_SEQ
      ? howItWorksSeq - HOW_IT_WORKS_GRID_START_SEQ
      : -1;

  const howItWorksSetupPhaseActive =
    howItWorksGridIx >= 0 && howItWorksGridIx < HOW_IT_WORKS_ASSIGN_START;
  const howItWorksAssignPhaseActive =
    howItWorksGridIx >= HOW_IT_WORKS_ASSIGN_START &&
    howItWorksGridIx < HOW_IT_WORKS_TRACK_START;
  const howItWorksTrackPhaseActive =
    howItWorksGridIx >= HOW_IT_WORKS_TRACK_START &&
    howItWorksGridIx < HOW_IT_WORKS_GRID_STEP_COUNT;

  const moreFeatures = [
    {
      title: "Modern, Responsive Design",
      description:
        "Beautiful UI that adapts to phones, tablets, and desktops for a seamless experience.",
      icon: "bi-aspect-ratio",
      badge: "New",
    },
    {
      title: "Dark Mode & Themes",
      description:
        "Toggle light/dark themes instantly; your preference is remembered across sessions.",
      icon: "bi-moon-stars",
      badge: null,
    },

    {
      title: "Calendar Subscriptions",
      description:
        "One-click iCal feeds for rotations, conferences, and personal events to external calendars.",
      icon: "bi-calendar-week",
      badge: null,
    },
    {
      title: "Export & Print Center",
      description:
        "Quickly export CSVs and print-ready views for schedules, cases, evaluations, and attendance.",
      icon: "bi-download",
      badge: null,
    },
    {
      title: "Resident Progression & Graduation",
      description:
        "Track PGY promotions, requirements completion, and graduation readiness with automated workflows.",
      icon: "bi-mortarboard",
      badge: null,
    },
    {
      title: "Full calendar suite",
      description:
        "Full calendar suite with one click export of generated rotations, conferences, leave dates, and more to your external calendar.",
      icon: "bi-command",
      badge: null,
    },
    {
      title: "End-to-End Encrypted Messaging",
      description:
        "Secure 1:1 and group messaging with end‑to‑end encryption, read receipts, and attachments.",
      icon: "bi-shield-lock-fill",
      badge: "Coming soon",
    },
    {
      title: "Mobile App — iOS",
      description:
        "Native iPhone app for logging hours/cases on the go, push alerts, and offline drafts.",
      icon: "bi-phone",
      badge: "Coming soon",
    },
    {
      title: "Mobile App — Android",
      description:
        "Native Android app with push notifications, quick actions, and offline support.",
      icon: "bi-phone",
      badge: "Coming soon",
    },
  ];

  // Animation state for notifications
  const [notificationIndex, setNotificationIndex] = useState(0);
  const [announcementIndex, setAnnouncementIndex] = useState(0);

  // Document upload animation state
  const [uploadedDocuments, setUploadedDocuments] = useState([
    {
      id: 1,
      name: "Resident Handbook 2024.pdf",
      size: "2.4 MB",
      type: "pdf",
      progress: 0,
      status: "processing",
      visible: false,
    },
    {
      id: 2,
      name: "Medical Conference Presentation.pptx",
      size: "5.2 MB",
      type: "ppt",
      progress: 0,
      status: "processing",
      visible: false,
    },
    {
      id: 3,
      name: "Rotation Schedule Template.docx",
      size: "892 KB",
      type: "doc",
      progress: 0,
      status: "processing",
      visible: false,
    },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionComplete, setShowSubmissionComplete] = useState(false);
  const [activeFeatureTab, setActiveFeatureTab] = useState("rotation");

  // Notification data for animation
  const notificationData = [
    {
      type: "urgent",
      icon: "bi-exclamation-triangle-fill",
      title: "3 evaluations due",
      time: "Due in 2 hours",
      badge: "3",
    },
    {
      type: "info",
      icon: "bi-calendar-event",
      title: "2 conferences tomorrow",
      time: "9:00 AM & 2:00 PM",
      badge: "2",
    },
    {
      type: "success",
      icon: "bi-check-circle-fill",
      title: "Work log submitted",
      time: "Just now",
      badge: null,
    },
    {
      type: "urgent",
      icon: "bi-clock",
      title: "Rotation evaluation overdue",
      time: "Due yesterday",
      badge: "1",
    },
    {
      type: "info",
      icon: "bi-person-check",
      title: "New resident assigned",
      time: "5 minutes ago",
      badge: null,
    },
  ];

  useEffect(() => {
    activeRoleRef.current = activeRole;
    if (activeRole) {
      orbitControlRef.current?.pause?.();
    } else {
      orbitControlRef.current?.resume?.();
    }
  }, [activeRole]);

  // Announcement data for animation
  const announcementData = [
    {
      title: "New Policy Update: Work Hour Regulations",
      description:
        "Updated guidelines for resident work hours and break requirements effective immediately.",
      time: "2 hours ago",
    },
    {
      title: "Conference Schedule Change",
      description:
        "Grand Rounds moved to Thursday 2:00 PM due to speaker availability.",
      time: "1 day ago",
    },
    {
      title: "Monthly Evaluation Reminder",
      description:
        "All monthly evaluations are due by Friday 5:00 PM. Please complete promptly.",
      time: "3 days ago",
    },
    {
      title: "New Rotation Requirements",
      description:
        "Updated competency requirements for Cardiology rotation starting next month.",
      time: "1 week ago",
    },
    {
      title: "System Maintenance Notice",
      description:
        "Scheduled maintenance this weekend. System will be unavailable Saturday 2-4 AM.",
      time: "2 weeks ago",
    },
  ];


  // Mockup images array (add more images as needed)
  const mockupImages = [
    {
      src: demoCapture0007,
      darkSrc: demoCapture0006,
      alt: "MedHive Resident Portal  - Main View",
      title: "Resident Portal",
    },
    {
      src: demoCapture0009,
      darkSrc: demoCapture0008,
      alt: "MedHive Evaluations - Main View",
      title: "Resident Evaluations",
    },
    {
      src: demoCapture0015,
      darkSrc: demoCapture0014,
      alt: "MedHive Calendar - Main View",
      title: "Rotation Scheduling",
    },
    {
      src: demoCapture0005,
      darkSrc: demoCapture0004,
      alt: "MedHive Schedule - Main View",
      title: "Schedule Management",
    },
    {
      src: demoCapture0013,
      darkSrc: demoCapture0012,
      alt: "MedHive Conference - Main View",
      title: "Weekly Conference view",
    },
    {
      src: demoCapture0011,
      darkSrc: demoCapture0010,
      alt: "MedHive Resources - Main View",
      title: "Resources and Helpful Links",
    },
    {
      src: demoCapture0003,
      darkSrc: demoCapture0002,
      alt: "MedHive Work Log - Main View",
      title: "Resident Work Hours",
    },
    {
      src: demoCapture0001,
      darkSrc: demoCapture0000,
      alt: "MedHive Case Log - Main View",
      title: "Case Log Management",
    },
    // Add more images here when available
    // { src: resDashboard2, alt: "MedHive Dashboard - Rotations" },
    // { src: resDashboard3, alt: "MedHive Dashboard - Evaluations" },
  ];

  // Get all specialties for rotation
  const specialties = Object.keys(SPECIALTY_ROTATION_TYPES);

  // Rotate specialties every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSpecialtyIndex(
        (prevIndex) => (prevIndex + 1) % specialties.length,
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [specialties.length]);
  // Typewriter effect for feature phrases in features header
  useEffect(() => {
    // Pause the typewriter when the features section is not visible
    if (!isFeaturesVisible) {
      return undefined;
    }

    const phrase = FEATURE_PHRASES[currentFeatureIndex];
    let charIndex = 0;
    setTypedFeatureText("");

    const typeInterval = setInterval(() => {
      charIndex += 1;
      setTypedFeatureText(phrase.slice(0, charIndex));

      if (charIndex >= phrase.length) {
        clearInterval(typeInterval);
        // After a short pause, move to the next phrase
        setTimeout(() => {
          setCurrentFeatureIndex((prev) => (prev + 1) % FEATURE_PHRASES.length);
        }, 1500);
      }
    }, 80);

    return () => {
      clearInterval(typeInterval);
    };
  }, [currentFeatureIndex, isFeaturesVisible]);

  // Start scrolling animation for current image
  const startScrolling = () => {
    if (!scrollContainerRef.current) {
      console.log("No scroll container ref");
      return;
    }

    const container = scrollContainerRef.current;
    const image = container.querySelector(".mockup-image");
    if (!image) {
      console.log("No image found in container");
      return;
    }

    console.log("Starting scroll animation for image:", currentImageIndex);
    // Reset scroll position to top
    container.scrollTop = 0;
    container.scrollTo({ top: 0, behavior: "auto" });
    setIsScrolling(true);

    // Clear any existing scroll interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }

    // Start smooth scrolling from top to bottom (slower)
    const scrollStep = 2; // pixels per step (more noticeable)
    const scrollInterval = 50; // ~20fps (slower)

    scrollIntervalRef.current = setInterval(() => {
      const maxScroll = image.scrollHeight - container.clientHeight;
      const currentScroll = container.scrollTop;

      console.log("Scroll values:", {
        scrollTop: currentScroll,
        maxScroll: maxScroll,
        imageHeight: image.scrollHeight,
        containerHeight: container.clientHeight,
      });

      if (currentScroll >= maxScroll) {
        // Reached bottom, stop scrolling
        console.log("Reached bottom, stopping scroll");
        clearInterval(scrollIntervalRef.current);
        setIsScrolling(false);
      } else {
        // Use scrollTo for better compatibility
        container.scrollTo({
          top: currentScroll + scrollStep,
          behavior: "auto",
        });
      }
    }, scrollInterval);
  };

  // Stop scrolling animation
  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    setIsScrolling(false);
  };

  // Auto-rotate mockup images every 15 seconds (allowing time for scrolling)
  useEffect(() => {
    if (mockupImages.length > 1) {
      const interval = setInterval(() => {
        resetScrollPosition();
        fadeInImage();
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % mockupImages.length,
        );
      }, 15000); // 15 seconds to allow 5s delay + 10s scrolling

      return () => clearInterval(interval);
    }
  }, [mockupImages.length]);

  // Start scrolling when image changes
  useEffect(() => {
    const timer = setTimeout(() => {
      startScrolling();
    }, 5000); // Wait 5 seconds after image change, then start scrolling

    return () => {
      clearTimeout(timer);
      stopScrolling();
    };
  }, [currentImageIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScrolling();
    };
  }, [activeFeatureTab]);

  // Reset scroll position to top
  const resetScrollPosition = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  // Fade in new image
  const fadeInImage = () => {
    setImageOpacity(0);
    setTimeout(() => {
      setImageOpacity(1);
    }, 50);
  };

  const closeRolePopup = () => {
    setActiveRole(null);
  };

  useEffect(() => {
    if (!activeRole) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRolePopup();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeRole]);

  const featuresSectionRef = useRef(null);
  const landingPageRootRef = useRef(null);

  // Track visibility of the features section to pause animations when off-screen
  useEffect(() => {
    const sectionEl = featuresSectionRef.current;
    if (!sectionEl) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsFeaturesVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.2 },
    );

    observer.observe(sectionEl);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleDemoInputChange = (e) => {
    const { id, value } = e.target;
    let key = id.replace("demo", "");
    if (key === "DateTime") key = "dateTime";
    else key = key.charAt(0).toLowerCase() + key.slice(1);
    setDemoForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setDemoLoading(true);
    setDemoError("");
    setDemoSuccess(false);
    try {
      // Split name into first/last
      const [firstName, ...lastArr] = demoForm.name.trim().split(" ");
      const lastName = lastArr.join(" ");
      const payload = {
        firstName: firstName || "",
        lastName: lastName || "",
        email: demoForm.email,
        organization: demoForm.company,
        preferredDateTime: demoForm.dateTime,
        message: demoForm.message,
        contactType: "demo",
      };
      const res = await fetch(API_ENDPOINTS.CONTACT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setDemoError(data.message || "Failed to send your request.");
        setDemoLoading(false);
        return;
      }
      setDemoSuccess(true);
      setDemoForm({
        name: "",
        email: "",
        company: "",
        dateTime: "",
        message: "",
      });
      setTimeout(() => setDemoSuccess(false), 5000);
    } catch (err) {
      setDemoError("Network error. Please try again.");
    } finally {
      setDemoLoading(false);
    }
  };

  // Theme toggle handler
  const handleThemeToggle = () => {
    setDarkMode((prev) => !prev);
  };

  // Handle theme changes
  useEffect(() => {
    document.body.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    setIsDarkMode(darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Check for dark mode preference using the app's custom system
    const checkDarkMode = () => {
      // Check localStorage first (app's custom theme system)
      const storedTheme = localStorage.getItem("theme");
      const isDark = storedTheme ? storedTheme === "dark" : false; // default to light for landing page
      setIsDarkMode(isDark);
      setDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for changes to localStorage (when user toggles theme in navbar)
    const handleStorageChange = (e) => {
      if (e.key === "theme") {
        checkDarkMode();
      }
    };

    // Listen for changes to data-bs-theme attribute
    const observer = new MutationObserver(() => {
      checkDarkMode();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-bs-theme"],
    });

    window.addEventListener("storage", handleStorageChange);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("storage", handleStorageChange);
      observer.disconnect();
    };
  }, [scrolled]);

  useEffect(() => {
    const animateCounters = () => {
      const counters = document.querySelectorAll(".stat-number");
      const speed = 200;

      counters.forEach((counter) => {
        const target = +counter.getAttribute("data-count");
        const count = +counter.innerText;
        const increment = target / speed;

        if (count < target) {
          counter.innerText = Math.ceil(count + increment);
          setTimeout(animateCounters, 1);
        } else {
          counter.innerText = target;
        }
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 },
    );

    const statsSection = document.querySelector(".stats-section");
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => {
      if (statsSection) {
        observer.unobserve(statsSection);
      }
    };
  }, []);

  // Animation loops for notifications and announcements
  useEffect(() => {
    const notificationInterval = setInterval(() => {
      setNotificationIndex((prev) => (prev + 1) % notificationData.length);
    }, 12000); // 12 seconds

    const announcementInterval = setInterval(() => {
      setAnnouncementIndex((prev) => (prev + 1) % announcementData.length);
    }, 12000); // 12 seconds

    return () => {
      clearInterval(notificationInterval);
      clearInterval(announcementInterval);
    };
  }, [notificationData.length, announcementData.length]);

  // Team hierarchy chart (SVG)
  useEffect(() => {
    if (activeFeatureTab !== "roles") {
      orbitControlRef.current = { pause: () => {}, resume: () => {} };
      return undefined;
    }

    let rafId = null;
    let cleanupFn = null;

    const initChart = () => {
      const svg = hierarchyChartRef.current;
      if (!svg) {
        rafId = requestAnimationFrame(initChart);
        return;
      }

      const NS = "http://www.w3.org/2000/svg";
      const HTML_NS = "http://www.w3.org/1999/xhtml";
      const tooltipTargets = [];
      const tooltipInstances = [];
      const iconHoverTargets = [];
      const interactionCleanupFns = [];
      const cx = 400;
      const cy = 410;
      const r1 = 135;
      const r2 = 258;
      let animationFrameId = null;
      let isOrbitPaused = false;
      let lastTimestamp = null;
      let totalElapsed = 0;
      let hoverCount = 0;

      const pauseOrbit = () => {
        isOrbitPaused = true;
      };

      const resumeOrbit = () => {
        if (isOrbitPaused) {
          isOrbitPaused = false;
          lastTimestamp = null;
        }
      };

      const requestResume = () => {
        if (hoverCount === 0 && !activeRoleRef.current) {
          resumeOrbit();
        }
      };

      orbitControlRef.current = {
        pause: () => pauseOrbit(),
        resume: () => requestResume(),
      };

      const toXY = (angle, r) => {
        const rad = (angle * Math.PI) / 180;
        return { x: Math.cos(rad) * r, y: Math.sin(rad) * r };
      };

      const el = (tag, attrs = {}) => {
        const e = document.createElementNS(NS, tag);
        for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
        return e;
      };

      while (svg.firstChild) svg.removeChild(svg.firstChild);

      const defs = el("defs");
      [
        ["orb1", "#94a3b8", "0.13", "0.38"],
        ["orb2", "#64748b", "0.07", "0.22"],
        ["orb3", "#475569", "0.03", "0.10"],
      ].forEach(([id, color, op1]) => {
        const rg = el("radialGradient", { id, cx: "50%", cy: "50%", r: "50%" });
        rg.append(
          el("stop", { offset: "60%", "stop-color": color, "stop-opacity": op1 }),
          el("stop", { offset: "100%", "stop-color": color, "stop-opacity": "0" })
        );
        defs.appendChild(rg);
      });
      const filt = el("filter", { id: "softglow" });
      const blur = el("feGaussianBlur", { stdDeviation: "4", result: "blur" });
      const merge = el("feMerge");
      merge.append(el("feMergeNode", { in: "blur" }), el("feMergeNode", { in: "SourceGraphic" }));
      filt.append(blur, merge);
      defs.appendChild(filt);
      svg.appendChild(defs);

      [
        { r: r1, fill: "url(#orb1)", stroke: "#94a3b8", sw: "0.7", op: "0.35" },
        { r: r2, fill: "url(#orb2)", stroke: "#64748b", sw: "0.5", op: "0.22" },
        { r: r2 + 60, fill: "url(#orb3)", stroke: "#475569", sw: "0.3", op: "0.10" },
      ].forEach(({ r, fill, stroke, sw, op }) => {
        svg.appendChild(el("circle", { cx, cy, r, fill, stroke, "stroke-width": sw, opacity: op }));
      });

      const linesGroup = el("g", { class: "hierarchy-lines" });
      svg.appendChild(linesGroup);

      const createLine = (attrs = {}) => {
        const line = el("line", attrs);
        linesGroup.appendChild(line);
        return line;
      };
      const registerInteraction = (target, type, handler) => {
        target.addEventListener(type, handler);
        interactionCleanupFns.push(() => target.removeEventListener(type, handler));
      };

      const midNodes = [
        { label: ["Program", "Director"], angle: -90, color: "#3b6fd4" },
        { label: ["Attending"], angle: 30, color: "#0f9e8a" },
        { label: ["Residents"], angle: 150, color: "#8b5cf6" },
      ].map((n) => ({ ...n, radius: r1 }));

      const angleDifference = (a, b) => {
        const diff = ((a - b + 540) % 360) - 180;
        return Math.abs(diff);
      };

      const outerNodes = [
        { id: "resident-features", label: ["Log", "Duty Hours"], icon: "bi-clock-history", angle: -130, color: "var(--red)", description: "Submit, view, and manage ACGME-compliant work hours. Receive instant feedback on violations and trends." },
        { id: "resident-evals", label: ["Complete", "Evaluations"], icon: "bi-clipboard-check", angle: -90, color: "var(--yellow)", description: "Fill out or view performance evaluations on rotations, peers, and faculty. Track milestones and feedback." },
        { id: "resident-cases", label: ["Log", "Cases"], icon: "bi-journal-medical", angle: -50, color: "var(--blue)", description: "Enter, review, and analyze case logs for ACGME and specialty requirements, view operative progress." },
        { id: "director-audit", label: ["Review", "Progress"], icon: "bi-bar-chart-line", angle: -10, color: "var(--primary)", description: "Access dashboards to monitor resident work hours, case experience, evaluation completion, and compliance." },
        { id: "director-signoff", label: ["Sign Off", "Evaluations"], icon: "bi-pen", angle: 30, color: "var(--indigo)", description: "Approve, reject, or annotate evaluations, onboarding tasks, and duty hour attestations." },
        { id: "director-alerts", label: ["Manage", "Alerts"], icon: "bi-bell", angle: 70, color: "var(--orange)", description: "Set automated reminders and intervention alerts for residents and faculty." },
        { id: "attending-evaluate", label: ["Evaluate", "Residents"], icon: "bi-person-check-fill", angle: 110, color: "var(--success)", description: "Complete milestone-based resident evaluations and provide formative feedback." },
        { id: "attending-cosign", label: ["Co-sign", "Logs"], icon: "bi-pencil-square", angle: 150, color: "var(--teal)", description: "Verify and sign off procedure, case, or duty hour logs as needed for credentialing." },
        { id: "attending-education", label: ["Education", "Sessions"], icon: "bi-easel2", angle: 190, color: "var(--violet)", description: "Lead/track conference attendance, assign readings, and manage resident teaching activities." },
      ].map((n) => {
        const nearestMidIndex = midNodes.reduce(
          (closest, mid, idx) => {
            const diff = angleDifference(n.angle, mid.angle);
            if (diff < closest.diff) return { idx, diff };
            return closest;
          },
          { idx: 0, diff: Infinity },
        ).idx;
        return { ...n, radius: r2, nearestMidIndex };
      });

      const makeNode = (x, y, labels, r, color, halo = false, icon, tooltipLabel, options = {}) => {
        const g = el("g", { transform: `translate(${x},${y})`, class: "node" });
        if (options.onClick) {
          g.classList.add("node-interactive");
          g.setAttribute("tabindex", "0");
          g.setAttribute("role", "button");
          if (tooltipLabel) g.setAttribute("aria-label", tooltipLabel);
          const handleGroupClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            options.onClick();
          };
          const handleGroupKey = (event) => {
            if (event.key === "Enter" || event.key === " " ) {
              handleGroupClick(event);
            }
          };
          registerInteraction(g, "click", handleGroupClick);
          registerInteraction(g, "keydown", handleGroupKey);
        }
        if (labels?.length) {
          const title = el("title");
          title.textContent = labels.join(" " );
          g.appendChild(title);
        }
        if (halo) {
          g.appendChild(el("circle", { r: r + 16, fill: "none", stroke: color, "stroke-width": "1", opacity: "0.18" }));
        }
        const c = el("circle", {
          r,
          fill: color,
          class: "main-circle",
          style: `filter: drop-shadow(0 2px 8px ${color}44);`,
        });
        g.appendChild(c);
        g.addEventListener("mouseenter", () => {
          c.style.filter = `drop-shadow(0 0 20px ${color}cc)`;
          c.style.opacity = "1";
        });
        g.addEventListener("mouseleave", () => {
          c.style.filter = `drop-shadow(0 2px 8px ${color}44)`;
          c.style.opacity = "0.92";
        });
        if (icon) {
          const fo = el("foreignObject", { x: -r, y: -r, width: r * 2, height: r * 2 });
          const wrapper = document.createElementNS(HTML_NS, "div");
          wrapper.setAttribute("xmlns", HTML_NS);
          wrapper.setAttribute("style", "width:100%;height:100%;display:flex;align-items:center;justify-content:center;");
          if (options.onClick) {
            wrapper.classList.add("hierarchy-node-button");
            wrapper.setAttribute("tabindex", "0");
            wrapper.setAttribute("role", "button");
            if (tooltipLabel) {
              wrapper.setAttribute("aria-label", tooltipLabel);
            }
          }
          const iconEl = document.createElementNS(HTML_NS, "i");
          iconEl.setAttribute("class", `bi ${icon}`);
          iconEl.setAttribute("aria-hidden", "true");
          iconEl.setAttribute("style", `font-size:${r >= 30 ? 22 : 18}px;color:#fff;`);
          wrapper.appendChild(iconEl);
          if (tooltipLabel) {
            wrapper.setAttribute("data-bs-toggle", "tooltip");
            wrapper.setAttribute("data-bs-title", tooltipLabel);
            tooltipTargets.push(wrapper);
            iconHoverTargets.push(wrapper);
          }
          if (options.onClick) {
            const handleClick = (event) => {
              event.preventDefault();
              event.stopPropagation();
              options.onClick();
            };
            const handleKeyDown = (event) => {
              if (event.key === "Enter" || event.key === " " ) {
                event.preventDefault();
                options.onClick();
              }
            };
            registerInteraction(wrapper, "click", handleClick);
            registerInteraction(wrapper, "keydown", handleKeyDown);
          }
          fo.appendChild(wrapper);
          g.appendChild(fo);
        } else if (labels?.length) {
          const fontSize = r > 45 ? "12" : r > 32 ? "10.5" : "9";
          labels.forEach((line, i) => {
            const offsetY = labels.length > 1 ? (i - (labels.length - 1) / 2) * 13 + 1 : 5;
            const t = el("text", {
              x: "0",
              y: offsetY,
              "text-anchor": "middle",
              fill: "#fff",
              "font-size": fontSize,
              "font-family": "'DM Sans', sans-serif",
              "font-weight": "600",
              "letter-spacing": "0.3",
              style: "pointer-events:none;",
            });
            t.textContent = line;
            g.appendChild(t);
          });
        }
        return g;
      };

      const midNodeStates = midNodes.map((n) => {
        const { x, y } = toXY(n.angle, n.radius);
        const radialLine = createLine({
          x1: cx,
          y1: cy,
          x2: cx + x,
          y2: cy + y,
          stroke: "#94a3b8",
          "stroke-width": "1",
          "stroke-dasharray": "4 5",
          opacity: "0.5",
        });
        const node = makeNode(cx + x, cy + y, n.label, 42, n.color, true);
        svg.appendChild(node);
        return {
          ...n,
          element: node,
          lineToCenter: radialLine,
          baseAngle: n.angle,
          currentX: cx + x,
          currentY: cy + y,
        };
      });

      const outerNodeStates = outerNodes.map((n) => {
        const { x, y } = toXY(n.angle, n.radius);
        const midRef = midNodeStates[n.nearestMidIndex];
        const connector = createLine({
          x1: midRef.currentX,
          y1: midRef.currentY,
          x2: cx + x,
          y2: cy + y,
          stroke: "#64748b",
          "stroke-width": "0.9",
          "stroke-dasharray": "4 5",
          opacity: "0.3",
        });
        const tooltipLabel = n.label.join(" " );
        const node = makeNode(
          cx + x,
          cy + y,
          n.label,
          30,
          n.color,
          false,
          n.icon,
          tooltipLabel,
          {
            onClick: () => {
              orbitControlRef.current?.pause?.();
              setActiveRole({
                id: n.id,
                title: tooltipLabel,
                description: n.description,
                icon: n.icon,
                color: n.color,
              });
            },
          },
        );
        svg.appendChild(node);
        return {
          ...n,
          element: node,
          connector,
          baseAngle: n.angle,
          currentX: cx + x,
          currentY: cy + y,
        };
      });

      const midOrbitDuration = 36;
      const outerOrbitDuration = 48;
      const midAngularSpeed = 360 / midOrbitDuration;
      const outerAngularSpeed = 360 / outerOrbitDuration;

      const animateOrbits = (timestamp) => {
        if (lastTimestamp === null) {
          lastTimestamp = timestamp;
        }

        if (isOrbitPaused) {
          lastTimestamp = timestamp;
          animationFrameId = requestAnimationFrame(animateOrbits);
          return;
        }

        const delta = timestamp - lastTimestamp;
        totalElapsed += delta;
        lastTimestamp = timestamp;
        const elapsedSeconds = totalElapsed / 1000;

        midNodeStates.forEach((node) => {
          const { x, y } = toXY(node.baseAngle + elapsedSeconds * midAngularSpeed, node.radius);
          const absX = cx + x;
          const absY = cy + y;
          node.currentX = absX;
          node.currentY = absY;
          node.element.setAttribute("transform", `translate(${absX},${absY})`);
          node.lineToCenter.setAttribute("x2", absX);
          node.lineToCenter.setAttribute("y2", absY);
        });

        outerNodeStates.forEach((node) => {
          const { x, y } = toXY(node.baseAngle + elapsedSeconds * outerAngularSpeed, node.radius);
          const absX = cx + x;
          const absY = cy + y;
          node.currentX = absX;
          node.currentY = absY;
          node.element.setAttribute("transform", `translate(${absX},${absY})`);
          const midRef = midNodeStates[node.nearestMidIndex];
          node.connector.setAttribute("x1", midRef.currentX);
          node.connector.setAttribute("y1", midRef.currentY);
          node.connector.setAttribute("x2", absX);
          node.connector.setAttribute("y2", absY);
        });

        animationFrameId = requestAnimationFrame(animateOrbits);
      };

      animationFrameId = requestAnimationFrame(animateOrbits);
      svg.appendChild(el("circle", { cx, cy, r: 72, fill: "none", stroke: "#6366f1", "stroke-width": "1.5", opacity: "0.3", filter: "url(#softglow)" }));
      svg.appendChild(el("circle", { cx, cy, r: 82, fill: "none", stroke: "#6366f1", "stroke-width": "0.5", opacity: "0.12" }));
      svg.appendChild(makeNode(cx, cy, ["Residency", "Program"], 54, "var(--black)", true));
      svg.appendChild(el("circle", { cx, cy, r: 57, fill: "none", stroke: "#818cf8", "stroke-width": "1.8", opacity: "0.5" }));
      tooltipTargets.forEach((target) => {
        tooltipInstances.push(
          new Tooltip(target, {
            trigger: "hover focus",
            placement: "top",
            container: target.closest(".hierarchy-chart-container") || document.body,
          }),
        );
      });
      const hoverHandlers = iconHoverTargets.map((target) => {
        const onEnter = () => {
          hoverCount += 1;
          orbitControlRef.current?.pause?.();
        };
        const onLeave = () => {
          hoverCount = Math.max(0, hoverCount - 1);
          orbitControlRef.current?.resume?.();
        };
        target.addEventListener("mouseenter", onEnter);
        target.addEventListener("mouseleave", onLeave);
        return { target, onEnter, onLeave };
      });

      cleanupFn = () => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        tooltipInstances.forEach((instance) => instance.dispose());
        hoverHandlers.forEach(({ target, onEnter, onLeave }) => {
          target.removeEventListener("mouseenter", onEnter);
          target.removeEventListener("mouseleave", onLeave);
        });
        interactionCleanupFns.forEach((fn) => fn());
        orbitControlRef.current = { pause: () => {}, resume: () => {} };
      };
    };

    initChart();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      cleanupFn?.();
    };
  }, [activeFeatureTab]);

// Document upload animation effect
// Document upload animation effect - loops every 15 seconds
  useEffect(() => {
    const resetDocuments = () => {
      setUploadedDocuments([
        {
          id: 1,
          name: "Resident Handbook 2024.pdf",
          size: "2.4 MB",
          type: "pdf",
          progress: 0,
          status: "processing",
          visible: false,
        },
        {
          id: 2,
          name: "Medical Conference Presentation.pptx",
          size: "5.2 MB",
          type: "ppt",
          progress: 0,
          status: "processing",
          visible: false,
        },
        {
          id: 3,
          name: "Rotation Schedule Template.docx",
          size: "892 KB",
          type: "doc",
          progress: 0,
          status: "processing",
          visible: false,
        },
      ]);
      setIsSubmitting(false);
      setShowSubmissionComplete(false);
    };

    const animateDocuments = () => {
      let completedCount = 0;
      const totalDocs = uploadedDocuments.length;

      uploadedDocuments.forEach((doc, index) => {
        // Show document after delay
        setTimeout(() => {
          setUploadedDocuments((prev) =>
            prev.map((d) => (d.id === doc.id ? { ...d, visible: true } : d)),
          );

          // Start progress animation
          setTimeout(() => {
            const progressInterval = setInterval(() => {
              setUploadedDocuments((prev) =>
                prev.map((d) => {
                  if (d.id === doc.id && d.progress < 100) {
                    const newProgress = Math.min(
                      d.progress + Math.random() * 15 + 5,
                      100,
                    );
                    return { ...d, progress: newProgress };
                  }
                  return d;
                }),
              );
            }, 200);

            // Complete upload after progress reaches 100
            setTimeout(
              () => {
                clearInterval(progressInterval);
                setUploadedDocuments((prev) =>
                  prev.map((d) =>
                    d.id === doc.id
                      ? { ...d, status: "success", progress: 100 }
                      : d,
                  ),
                );

                completedCount++;

                // Check if all documents are completed
                if (completedCount === totalDocs) {
                  // Wait a moment then start submission animation
                  setTimeout(() => {
                    setIsSubmitting(true);

                    // Animate documents disappearing
                    setTimeout(() => {
                      setUploadedDocuments((prev) =>
                        prev.map((d) => ({ ...d, visible: false })),
                      );

                      // Show submission complete message immediately as sections start disappearing
                      setShowSubmissionComplete(true);

                      // After animation completes, add class to completely hide sections
                      setTimeout(() => {
                        const onboardingSection = document.querySelector(
                          ".onboarding-package-section.section-hidden",
                        );
                        const docsSection = document.querySelector(
                          ".docs-section.section-hidden",
                        );
                        if (onboardingSection)
                          onboardingSection.classList.add("animation-complete");
                        if (docsSection)
                          docsSection.classList.add("animation-complete");
                      }, 800); // Match the CSS animation duration
                    }, 1000);
                  }, 1500);
                }
              },
              3000 + Math.random() * 2000,
            ); // 3-5 seconds total
          }, 500); // Start progress after document appears
        }, index * 1000); // Stagger document appearances by 1 second
      });
    };

    // Initial animation
    const startTimeout = setTimeout(animateDocuments, 2000);

    // Loop animation every 15 seconds
    const loopInterval = setInterval(() => {
      resetDocuments();
      setTimeout(animateDocuments, 2000); // Start animation after reset
    }, 15000);

    return () => {
      clearTimeout(startTimeout);
      clearInterval(loopInterval);
    };
  }, []);

  // Fade/slide sections into view as the user scrolls (see .landing-scroll-js in CSS)
  useEffect(() => {
    const root = landingPageRootRef.current;
    if (!root) return undefined;

    // Avoid :scope — not all browsers return matches reliably on element roots
    const targets = Array.from(root.children).filter(
      (el) =>
        el.nodeName === "SECTION" && !el.classList.contains("hero-section"),
    );
    if (!targets.length) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduceMotion) {
      targets.forEach((el) => el.classList.add("scroll-reveal--visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("scroll-reveal--visible");
          } else {
            entry.target.classList.remove("scroll-reveal--visible");
          }
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -2% 0px",
        threshold: 0.08,
      },
    );

    // Enable hidden state after paint so transitions run when .scroll-reveal--visible is added
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      root.classList.add("landing-scroll-js");
      raf2 = window.requestAnimationFrame(() => {
        targets.forEach((el) => observer.observe(el));
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      observer.disconnect();
      root.classList.remove("landing-scroll-js");
      targets.forEach((el) => el.classList.remove("scroll-reveal--visible"));
    };
  }, []);

  const navbarShouldBeScrolled = scrolled || navbarExpanded;

  return (
    <div className="landing-page" ref={landingPageRootRef}>
      <PageTitle
        title="Medhive - Residency Management Platform"
        description="Transform how you manage your residency program with an all-in-one platform for rotation scheduling, evaluations, work hours, and compliance monitoring."
      />
      {/* Navigation Bar */}
       
      <nav
        className={`navbar custom-navbar navbar-expand-lg navbar-light fixed-top${navbarShouldBeScrolled ? " scrolled" : ""}`}
        style={{ zIndex: 1050 }}
      >
        <div className="container">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/">
            <img
              src={isDarkMode ? "/images/asset4.svg" : "/images/asset5.svg"}
              alt="brand logo"
              style={{ height: "16px"}}
            />

            {/*<img
              src={navbarShouldBeScrolled ? "/images/logo5.svg" : "/images/logo4.svg"}
              alt="brand logo"
              style={{ width: "32px", height: "32px", marginTop: "-4px" }}
            />
            <span className="ms-2">medhive</span>*/}
          </Link>
          <div className="d-flex align-items-center ms-auto order-lg-2">
            <button
              className="btn btn-light-lite px-3"
              onClick={handleThemeToggle}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? (
                <i className="bi bi-sun-fill"></i>
              ) : (
                <i className="bi bi-moon-fill text-dark"></i>
              )}
            </button>
            <Link to="/login" className="btn btn-sm btn-primary ms-2">
              Login
            </Link>
            <button
              className="navbar-toggler btn-sm ms-2"
              type="button"
              aria-controls="mainNavbar"
              aria-expanded={navbarExpanded}
              aria-label="Toggle navigation"
              onClick={() => setNavbarExpanded((prev) => !prev)}
            >
              <span>
                <i
                  className={
                    navbarShouldBeScrolled
                      ? "bi bi-list"
                      : "bi bi-list opacity-50"
                  }
                ></i>
              </span>
            </button>
          </div>
          <div
            className={`collapse navbar-collapse justify-content-center order-lg-1${navbarExpanded ? " show" : ""}`}
            id="mainNavbar"
          >
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4 gap-2 align-items-lg-center text-center">
              <li className="nav-item">
                <Link
                  to="/"
                  className="nav-link"
                  onClick={() => setNavbarExpanded(false)}
                >
                  Home
                </Link>
              </li>
              <li className="nav-item">
                <a
                  href="#features"
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("features")
                      ?.scrollIntoView({ behavior: "smooth" });
                    setNavbarExpanded(false);
                  }}
                >
                  Features
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#pricing"
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("pricing")
                      ?.scrollIntoView({ behavior: "smooth" });
                    setNavbarExpanded(false);
                  }}
                >
                  Pricing
                </a>
              </li>
              <li className="nav-item">
                <Link
                  to="/about"
                  className="nav-link"
                  onClick={() => setNavbarExpanded(false)}
                >
                  About Us
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  to="/contact"
                  className="nav-link"
                  onClick={() => setNavbarExpanded(false)}
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
     <section className="hero-section">
        {/*<div className="hero-background-elements">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
        </div>*/}

        <div className="container-fluid d-flex align-items-center justify-content-center">
          <div className="row">
            <div className="col-12 min-vh-60 d-flex flex-wrap flex-column align-items-center justify-content-center text-center">
                {/* Residency Management Badge */}
                  <div className="hero-title-container d-flex gap-1 gap-md-2 flex-wrap flex-column align-items-center justify-content-center">
                  <h1 className="fw-bold">
                  Residency Management, Reimagined for your{" "} <br />
                  <span className="hero-title-highlight">
                    {specialties[currentSpecialtyIndex]}
                  </span>{" "}
                  residency program
                </h1>

                <p className="text-muted">
                  Transform how you manage your residency program with an
                  all-in-one platform that effortlessly schedules rotations,
                  tracks evaluations, logs work hours, and monitors case
                  experience. Organize conferences, manage vacation requests,
                  and ensure ACGME compliance with ease.
                </p>
                
                <div className="d-flex flex-row gap-3 justify-content-center align-items-center">
                  <Link
                    to="/signup"
                    className="main-btn main-btn-lg main-primary-btn"
                  >
                    <i className="bi bi-rocket-takeoff me-2"></i>
                    Get Started
                  </Link>
                  <Link
                    to="#"
                    onClick={playStorylaneDemo}
                    className="main-btn main-btn-lg main-secondary-btn"
                  >
                    <i className="bi bi-play-circle me-2"></i>
                    Take a Tour
                  </Link>
                {/* Centered mockup under hero text */}
              </div></div>
            </div>
              <div className="col-12 min-vh-40">
              <div className="hero-mockup min-vh-40">
                  <div className="browser-frame">
                    <div className="browser-header">
                      <div className="browser-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <div className="browser-title">
                        {mockupImages[currentImageIndex].title}
                      </div>
                    </div>

                    <div className="browser-body">
                      <div className="browser-navbar">
                        <div className="browser-navbar-placeholder">
                          <img
                            src={navbarPlaceholder}
                            alt="Navigation Bar"
                            className="navbar-placeholder-image"
                          />
                        </div>
                      </div>
                      <div className="mockup-carousel">
                        <div
                          className="scroll-container"
                          ref={scrollContainerRef}
                        >
                          <img
                            src={
                              isDarkMode && mockupImages[currentImageIndex].darkSrc
                                ? mockupImages[currentImageIndex].darkSrc
                                : mockupImages[currentImageIndex].src
                            }
                            alt={mockupImages[currentImageIndex].alt}
                            className="mockup-image"
                            style={{ opacity: imageOpacity }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Problem → Solution */}
      <section
        className="problem-solution-section"
        aria-labelledby="problem-solution-heading"
      >
        <div className="container py-5">
          <div className="problem-solution-intro text-center mb-4 mb-md-5">
            <span className="cta-badge mb-2 d-inline-flex align-items-center gap-1">
              <i className="bi bi-arrow-left-right text-primary" aria-hidden="true"></i>
              Why medhive
            </span>
            <h2 id="problem-solution-heading" className="fw-bold mt-2 mb-2">
              From Chaos to <span className="hero-title-highlight"> flow</span>
            </h2>
            <p className="text-muted mx-auto mb-0 problem-solution-lead">
            Open any row to see the everyday challenges residency programs face—and how Medhive transforms them into streamlined, connected workflows.
            </p>
          </div>
          <Row className="g-4 align-items-stretch problem-solution-grid">
            <Col lg={6}>
              <div className="problem-solution-panel problem-solution-panel--problem h-100">
                <div className="problem-solution-panel-head">
                  <span className="problem-solution-panel-icon bg-danger-subtle text-danger">
                    <i className="bi bi-exclamation-octagon-fill" aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="fw-bold problem-solution-panel-title mb-1">
                    Too many systems. Too much manual work.
                    </h3>
                    <p className="text-muted small mb-0">
                      Tool sprawl, paper trails, and work that does not carry forward
                    </p>
                  </div>
                </div>
                <Accordion
                  className="problem-solution-accordion landing-feature-accordion"
                  defaultActiveKey="0"
                >
                  {PROBLEM_SOLUTION_PROBLEMS.map((item, idx) => (
                    <Accordion.Item eventKey={String(idx)} key={item.title}>
                      <Accordion.Header>
                        <span className="problem-solution-accordion-title">
                          <i
                            className="bi bi-x-circle-fill text-danger me-2 flex-shrink-0"
                            aria-hidden="true"
                          ></i>
                          {item.title}
                        </span>
                      </Accordion.Header>
                      <Accordion.Body className="text-muted small">
                        {item.detail}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
                <div className="problem-solution-footnote text-muted small">
                  Fragmented workflows hide risk until survey season—then everything has to
                  be reconstructed by hand.
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="problem-solution-panel problem-solution-panel--solution h-100">
                <div className="problem-solution-panel-head">
                  <span className="problem-solution-panel-icon bg-success-subtle text-success">
                    <i className="bi bi-stars" aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="fw-bold problem-solution-panel-title mb-1">
                      Medhive fixes it
                    </h3>
                    <p className="text-muted small mb-0">
                      One place for schedules, education, compliance, and comms
                    </p>
                  </div>
                </div>
                <Accordion
                  className="problem-solution-accordion landing-feature-accordion"
                  defaultActiveKey="0"
                >
                  {PROBLEM_SOLUTION_SOLUTIONS.map((item, idx) => (
                    <Accordion.Item eventKey={String(idx)} key={item.title}>
                      <Accordion.Header>
                        <span className="problem-solution-accordion-title">
                          <i
                            className="bi bi-check-circle-fill text-success me-2 flex-shrink-0"
                            aria-hidden="true"
                          ></i>
                          {item.title}
                        </span>
                      </Accordion.Header>
                      <Accordion.Body className="text-muted small">
                        {item.detail}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
                <div className="problem-solution-footnote text-muted small">
                  Medhive turns those same workflows into a connected system of record—so
                  updates, attendance, and accountability stay tied to the people and dates
                  they belong to.
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

 

      {/* Features Section */}
      <section
        className="features-section"
        id="features"
        ref={featuresSectionRef}
      >
        <div className="container">
          <div className="text-start mb-3 features-header">
          <span class="cta-badge mb-2"> <i className="bi bi-stars text-warning"></i> features </span>
            <h2 className="fw-bold">
              Comprehensive{" "}
              <span className="hero-title-highlight typewriter-text">
                {typedFeatureText}
              </span>
              <span className="typewriter-caret">|</span>
              
                for your program
             
            </h2>
          </div>

          {/* Features Tabs */}
          <Tab.Container id="features-tabs" activeKey={activeFeatureTab} onSelect={(k) => setActiveFeatureTab(k || "rotation")}>
            <Nav variant="tabs" className="flex-wrap mb-4 features-nav-tabs">
              <Nav.Item><Nav.Link eventKey="rotation">Rotation Scheduling</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="onboarding">Document Onboarding</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="conference">Conference Scheduling</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="evaluation">Evaluations</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="workhours">Work Hour Logging</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="roles">Roles</Nav.Link></Nav.Item>
              {/*<Nav.Item><Nav.Link eventKey="caselog">Case Logging</Nav.Link></Nav.Item>*/}
              <Nav.Item><Nav.Link eventKey="notifications">Alerts & Notifications</Nav.Link></Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="rotation" mountOnEnter className="border-2 rounded-4 bg-card-srf-bg">
          <div className="row">
            <div className="col-lg-6 order-md-1">
              <div className="p-md-5 p-3">
                <div className="d-flex flex-column gap-3">
                  <div className="feature-icon bg-primary-solid">
                    <i className="bi bi-calendar-week"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Rotation Scheduling</h5>
                    <p className="text-muted">
                      Comprehensive rotation scheduling with drag-and-drop editing, automated call schedules, and calendar integration for residents.
                    </p>
                  </div>
                </div>
                <div className="feature-details">
                  <div>
           
                    <Accordion className="landing-feature-accordion">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          <i className="bi bi-calendar3-range me-2 text-primary"></i>
                          Flexible Rotation Planning
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <span>
                              Plan monthly, weekly, or block-based rotations with customizable durations and assignments.
                            </span>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="1">
                        <Accordion.Header>
                          <i className="bi bi-clock-history me-2 text-warning"></i>
                          Smart Call Scheduling
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <span>
                              Automate weekend and 24-hour call schedules using advanced algorithms and easily customize them with drag-and-drop.
                            </span>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="2">
                        <Accordion.Header>
                          <i className="bi bi-person-lines-fill me-2 text-info"></i>
                          Resident Schedule View
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <span>
                              Instantly view daily assignments for all residents at a glance.
                            </span>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="3">
                        <Accordion.Header>
                          <i className="bi bi-calendar-plus me-2 text-success"></i>
                          Easy Calendar Export
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <span>
                              Export rotations, conferences, vacation dates, and more to your external calendar with one click.
                            </span>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                  </div>
                  </div>
            </div>
            <div className="col-lg-6 order-md-2 py-md-5 py-3 d-flex align-items-center justify-content-center">
                 <div className="rotation-scheduler-feature-container d-flex flex-column justify-content-center">
                    <div className="gap-1 d-flex flex-column" >
                        <div className="d-flex flex-row gap-2 justify-content-between align-items-center">
                          <div className="d-flex flex-column align-items-center p-4">
                            <div className="feature-icon bg-info mb-2">
                              <i className="bi bi-stars text-info"></i>
                            </div>
                            <h5 style={{ color: 'var(--white)' }} >Generate Rotation Schedules</h5>
                            <p className="text-center" style={{ color: '#9fa7b3' }}>
                              {" "}
                              Create Rotation Schedules for your residents showing their
                              rotations, assignments and call schedules{" "}
                            </p>
                          </div>
                        
                      </div>
                      <div className="d-flex flex-column gap-1 mb-3">
                      <div className="rotation-row dir-left">
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            Elective
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Peds
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            SICU
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F97316" }}
                          >
                            Trauma
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#64748B" }}
                          >
                            Nights
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Vascular
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            ColoRectal
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#DC2626" }}
                          >
                            ACS
                          </div>
                        </div>
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            Elective
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Peds
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            SICU
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F97316" }}
                          >
                            Trauma
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#64748B" }}
                          >
                            Nights
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Vascular
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            ColoRectal
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#DC2626" }}
                          >
                            ACS
                          </div>
                        </div>
                      </div>
                      <div className="rotation-row dir-right">
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            Obstetrics
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            MFM
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            Gyn Surg
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F97316" }}
                          >
                            Urogyn
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#64748B" }}
                          >
                            GYN
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Fam Planning
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Endo
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#DC2626" }}
                          >
                            EM OB
                          </div>
                        </div>
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            Obstetrics
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            MFM
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            Gyn Surg
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F97316" }}
                          >
                            Urogyn
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#64748B" }}
                          >
                            GYN
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Fam Planning
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Endo
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#DC2626" }}
                          >
                            EM OB
                          </div>
                        </div>
                      </div>
                      <div className="rotation-row dir-left">
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#64748B" }}
                          >
                            Gen Peds
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            Neonatal
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            Peds EM
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F97316" }}
                          >
                            PICU
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Peds Cardio
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Peds Onc
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Peds Surg
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#DC2626" }}
                          >
                            Peds GI
                          </div>
                        </div>
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#64748B" }}
                          >
                            Gen Peds
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            Neonatal
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            Peds EM
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F97316" }}
                          >
                            PICU
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Peds Cardio
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Peds Onc
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Peds Surg
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#DC2626" }}
                          >
                            Peds GI
                          </div>
                        </div>
                      </div>
                      {/* Internal Medicine palette */}
                      <div className="rotation-row dir-right">
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#3B82F6" }}
                          >
                            Gen IM
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EF4444" }}
                          >
                            Cardio
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Pulm/CC
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            GI
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Nephro
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Endo
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EC4899" }}
                          >
                            Heme/Onc
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            ID
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EA580C" }}
                          >
                            ICU
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#6366F1" }}
                          >
                            Clinic
                          </div>
                        </div>
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#3B82F6" }}
                          >
                            Gen IM
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EF4444" }}
                          >
                            Cardio
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Pulm/CC
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            GI
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Nephro
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            Endo
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EC4899" }}
                          >
                            Heme/Onc
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#84CC16" }}
                          >
                            ID
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EA580C" }}
                          >
                            ICU
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#6366F1" }}
                          >
                            Clinic
                          </div>
                        </div>
                      </div>
                      {/* Pediatrics palette */}
                      <div className="rotation-row dir-left">
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#3B82F6" }}
                          >
                            Gen Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            Addiction
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EF4444" }}
                          >
                            Emerg Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Child Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            EM Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EC4899" }}
                          >
                            Out psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Forensic
                          </div>
                        </div>
                        <div className="rotation-track">
                          <div
                            className="rotation-chip"
                            style={{ background: "#3B82F6" }}
                          >
                            Gen Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#10B981" }}
                          >
                            Addiction
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EF4444" }}
                          >
                            Emerg Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#8B5CF6" }}
                          >
                            Child Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#F59E0B" }}
                          >
                            EM Psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#EC4899" }}
                          >
                            Out psych
                          </div>
                          <div
                            className="rotation-chip"
                            style={{ background: "#06B6D4" }}
                          >
                            Forensic
                          </div>
                        </div>
                      </div>
                    </div> 
                    </div>
             </div>
            </div>
          </div>
              </Tab.Pane>
              <Tab.Pane eventKey="onboarding" mountOnEnter className="border-2 rounded-4 p-3 bg-card-srf-bg">
          <div className="row ">
            <div className="col-12 col-md-8 col-lg-6 order-md-2 order-2">
              <div className="feature-image py-md-4 py-3">
                <div
                  className="image-placeholder-2 bg-light-card border-0"
                  style={{ position: "relative" }}
                >
                  <div className="placeholder-content">
                    {/* Document Packages */}
                    <div
                      className={`onboarding-package-section ${isSubmitting ? "submitting-section" : ""} ${showSubmissionComplete ? "section-hidden" : ""}`}
                    >
                      <div className="d-flex flex-row gap-2 align-items-center justify-content-start bg-light-card p-3">
                        <div className="packages-icon">
                          <i className="bi bi-folder-fill"></i>
                        </div>
                        <div className="d-flex flex-column align-items-start justify-content-start">
                          <h6 className="mb-0 fw-semibold">
                            New Resident Onboarding Package
                          </h6>
                          <p className="mb-0 text-start text-muted">
                           Required documents for new residents
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Drag & Drop Documents */}
                    <div
                      className={`docs-section mt-3 h-100${isSubmitting ? "submitting-section h-100" : ""} ${showSubmissionComplete ? "section-hidden h-100" : ""}`}
                    >
                      <div className="docs-card border-0 h-100">
                        <div className="row g-0">
                          <div className="col-12 p-3">
                            <div className="widget-header">
                              Uploaded Documents
                            </div>
                            <ul className="file-list list-unstyled mb-0">
                              {uploadedDocuments.map((doc) => (
                                <li
                                  key={doc.id}
                                  className={`file-item ${doc.status === "success" ? "completed" : "processing"} ${doc.visible ? "visible" : "hidden"} ${isSubmitting ? "submitting" : ""}`}
                                >
                                  <div className={`file-ext ext-${doc.type}`}>
                                    {doc.type.toUpperCase()}
                                  </div>
                                  <div className="file-meta">
                                    <div className="file-name">{doc.name}</div>
                                    <div className="file-size">{doc.size}</div>
                                    {doc.status === "processing" && (
                                      <div className="file-progress">
                                        <span
                                          className="bar"
                                          style={{ width: `${doc.progress}%` }}
                                        ></span>
                                      </div>
                                    )}
                                  </div>
                                  <div className={`file-status ${doc.status}`}>
                                    {doc.status === "success" ? (
                                      <i className="bi bi-check-circle-fill"></i>
                                    ) : (
                                      <i className="bi bi-arrow-clockwise"></i>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Submission Complete Message */}
                    {showSubmissionComplete && (
                      <div className="submission-complete-section h-100 ">
                        <div className="submission-complete h-100">
                          <div className="submission-icon">
                            <i className="bi bi-check-circle-fill"></i>
                          </div>
                          <div>
                            <h6 className="fw-semibold">Submission Complete!</h6>
                            <p className="text-muted">
                              All documents have been successfully submitted and
                              processed.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 order-md-1 order-1">
              <div className="feature-content">
                <div className="d-flex flex-column gap-2">
                  <div className="feature-icon bg-success-solid">
                    <i className="bi bi-file-earmark-text"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Document Onboarding & Compliance</h5>
                    <p className="text-muted">
                      Streamline onboarding with structured packets, enforced
                      requirements, and centralized tracking for residents and
                      faculty.
                    </p>
                  </div>
                </div>
                <div className="feature-details">
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="card rounded-4 bg-light-card h-100 border-0">
                        <div className="card-body d-flex flex-row align-items-start gap-2">
                          <div
                            className="rounded-circle bg-info"
                            style={{ padding: "0.5rem .75rem" }}
                          >
                            <i className="bi bi-ui-checks-grid bg-info"></i>
                          </div>
                          <div>
                            <div className="fw-semibold">
                              Customize packets and assign documents
                            </div>
                            <div className="text-muted small">
                              Create custom document packages or assign specific
                              documents to residents
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card rounded-4 bg-light-card h-100 border-0">
                        <div className="card-body d-flex flex-row align-items-start gap-2">
                          <div
                            className="rounded-circle bg-success"
                            style={{ padding: "0.5rem .75rem" }}
                          >
                            <i className="bi bi-lock-fill text-success "></i>
                          </div>
                          <div>
                            <div className="fw-semibold">
                              Garentee document Submission
                            </div>
                            <div className="text-muted small">
                              Lock Application until all documents are submitted
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card rounded-4 bg-light-card h-100 border-0">
                        <div className="card-body d-flex flex-row align-items-start gap-2">
                          <div
                            className="rounded-circle bg-primary"
                            style={{ padding: "0.5rem .75rem" }}
                          >
                            <i className="bi bi-cloud-arrow-up text-primary"></i>
                          </div>
                          <div>
                            <div className="fw-semibold">Upload Portal</div>
                            <div className="text-muted small">
                              Progress tracking & reminders
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
              </Tab.Pane>
              <Tab.Pane eventKey="conference" mountOnEnter className="border-2 rounded-4 bg-card-srf-bg">
          <div className="row d-flex align-items-center justify-content-center">
            <div className="col-12 col-md-8 col-lg-6 order-2 order-md-1">
              <div className="d-flex flex-column p-md-5 p-3 pb-2 gap-3">
                <div className="feature-icon bg-warning-solid">
                  <i className="bi bi-easel"></i>
                </div>
                <div>
                  <h5 className="fw-bold">Conference & Education Management</h5>
                  <p className="text-muted">
                    Plan and manage educational conferences with robust
                    scheduling, materials, and attendance tracking.
                  </p>
                </div>

                <div>
                  <p className="mb-0">
                  Streamlines academic conferences with recurring
                  scheduling, calendar export, presenter/event details,
                  attachments, and reminders, while offering attendance
                  tracking, and printable Reports—all designed to centralize
                  activities, boost participation, and ensure accurate records
                  for accreditation and CME credit.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-6 order-1 order-md-2 pe-5">
              <div className="feature-image position-relative">
                <div className="blob d-flex align-items-center justify-content-center z-1" >
                </div>
                <div className="blob-2" ></div>
              </div>
            </div>

            <div className="col-12 order-3">
                <div className="row p-md-5 p-3">
                  <div className="col-12 col-lg-3 col-md-6 mb-3 mb-lg-0">
                    <div className="bg-light-card rounded-4 p-2 ps-3 h-100">
                      <div className="conf-tile-icon mb-2">
                        <i className="bi bi-qr-code text-muted"></i>
                      </div>
                      <div className="conf-tile-title">
                        Multiple ways to record attendance
                      </div>
                      <div className="conf-tile-desc">
                        QR, manual, or code-based to ensure anyone can attend
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-3 col-md-6 mb-3 mb-lg-0">
                    <div className="bg-light-card rounded-4 p-2 ps-3 h-100">
                      <div className="conf-tile-icon mb-2">
                      <i className="bi bi-people text-muted"></i>
                      </div>
                      <div className="conf-tile-title">
                        Tracking & attendance
                      </div>
                      <div className="conf-tile-desc">
                        Assign specific attendees to each conferences and track
                        their attendance
                      </div>  
                    </div>
                  </div>
                  <div className="col-12 col-lg-3 col-md-6 mb-3 mb-md-0">
                  <div className="bg-light-card rounded-4 p-2 ps-3 h-100">
                    <div className="conf-tile-icon mb-2">
                      <i className="bi bi-folder2-open text-muted"></i>
                    </div>
                    <div className="conf-tile-title">
                      Robust conference management
                    </div>
                    <div className="conf-tile-desc">
                      Manage conference details, attachments, emails, and
                      calendar
                    </div>
                  </div>
                  </div>
                  <div className="col-12 col-lg-3 col-md-6">
                    <div className="bg-light-card rounded-4 p-2 ps-3 h-100">
                      <div className="conf-tile-icon mb-2">
                      <i className="bi bi-camera-video text-muted"></i>
                    </div>
                    <div className="conf-tile-title">Zoom Links</div>
                    <div className="conf-tile-desc">
                      Ensure everyone has access to the Zoom and can join the conference
                    </div>
                  </div>
                  </div>
                
              </div>
            </div>
          </div>
              </Tab.Pane>
              <Tab.Pane eventKey="evaluation" mountOnEnter className="border-2 rounded-4 bg-card-srf-bg">
                
          <div className="row">
            {/*<div className="col-lg-6 col-md-6 col-12 order-md-2 order-1">
              <div className="position-relative">
              <div className="eval-imag-background" >
                </div>
              </div>
              <div className="d-block d-md-none">
                <img src={evalImageMockup} alt="Evaluation" className="img-fluid" style={{borderRadius: '1rem 1rem 0 0'}}/>
              </div>
            </div>*/}
            
            <div className="col-lg-6 order-md-2 h-100">
              <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                <div className="eval-imag-container h-100 position-relative" >
                <img
                  src={isDarkMode ? evalMockupDark : evalMockup}
                  alt="Work Hours"
                  className="eval-imag"
                />
                </div></div>
            </div>

            <div className="col-lg-6 col-md-12 col-12 order-md-1 order-2">
              <div className="p-md-5 p-3">
                <div className="d-flex flex-column gap-3">
                  <div className="feature-icon bg-info-solid ">
                    <i className="bi bi-clipboard-check"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Comprehensive Evaluation System</h5>
                    <p className="text-muted mb-3">
                      Advanced evaluation platform supporting multiple
                      evaluation types with customizable forms and automated
                      assignment.
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-0">
                  Our platform supports multiple evaluation types with
                  customizable forms and automated assignment. It allows for
                  anonymous response options, timed evaluations, and recurring
                  scheduling for continuous assessments. These features help
                  standardize evaluation processes, enable timely feedback via
                  automation, and ensure ACGME compliance.
                  </p>
                </div>
              </div>
            </div> 
            </div>
              <div className="row px-md-5 p-3 pb-3 pb-md-4 ">
                <div className="col-md-6 col-12 mb-3">
                <div >
                  <div className="mb-2 fw-semibold">Field Types</div>
                  <div className="text-muted mb-2">
                    Build powerful, customized evaluations using these intuitive question formats.
                  </div>
                  <div className="d-flex eval feature-badges flex-wrap gap-2 ">
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-record-circle text-info"></i>Single Choice
                    </span>
                    <span className="badge d-flex align-items-center gap-1">
                      <i className="bi bi-caret-down-square text-success"></i>Dropdown
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-grid-3x3-gap text-primary"></i>Matrix Table
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-check2-square text-danger"></i>Multi-select
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-star-fill text-warning"></i>Rating
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-input-cursor-text text-primary"></i>Text Input
                    </span>
                  </div>
                </div>
                </div>
                <div className="col-md-6 col-12 mb-3"> 
                  <div >
                  <div className="mb-2 fw-semibold">Evaluation Types</div>
                  <div className="text-muted mb-2">
                    Multiple evaluation types to fit your program's needs
                  </div>
                  <div className="d-flex eval feature-badges flex-wrap gap-2 mb-3">
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-clipboard-check text-success"></i>Performance
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-chat-dots text-info"></i>Feedback
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-bar-chart text-secondary"></i>Survey
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-activity text-danger"></i>Case/Procedure
                    </span>
                    <span className="badge  d-flex align-items-center gap-1">
                      <i className="bi bi-calendar3 text-primary"></i>Rotation
                    </span>
                  </div>
                </div>
                </div>
              </div>
            
         
              </Tab.Pane>
              <Tab.Pane eventKey="workhours" mountOnEnter className="border-2 rounded-4 position-relative overflow-hidden bg-card-srf-bg">
                <div className="position-absolute s-circle-blur-2" style={{ top: '90%', right: '40%' }}></div>
                <div className="position-absolute s-circle-blur" style={{ top: '90%', right: '50%' }}></div>
          <div className="row">
            <div className="col-lg-6 order-md-1">
              <div className="feature-content p-md-5 p-3">
                <div className="d-flex flex-column gap-3">
                  <div className="feature-icon bg-danger-solid">
                    <i className="bi bi-graph-up"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Work Hour & Leave Management</h5>
                    <p className="text-muted mb-3">
                      Integrated work hour and leave management system with case
                      logging, real-time statistics, and director oversight
                      capabilities.
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-0">
                  Our platform supports weekly Hours logging with built-in compliance
                  monitoring, vacation and sick leave requests with approval workflows, and detailed statistics with printable reports.
                  These features help ensure ACGME work-hour
                  compliance and enables proactive intervention to address compliance issues.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-md-2 h-100">
              <div className="h-100 w-100 d-flex position-relative align-items-center justify-content-center">
                <img
                  src={isDarkMode ? workHoursMockupDark : workHoursMockup}
                  alt="Work Hours"
                  className="work-hour-img"
                />
                </div>
            </div>
            {/*<div className="col-lg-6 order-md-2 mb-5">
              <div className="feature-image">
                <div className="work-hours-dashboard-container bg-light-card">

                  <div className="quick-actions">
                    <button className="action-btn primary">
                      <i className="bi bi-plus-circle"></i>
                      <span>Log Hours</span>
                    </button>
                    <button className="action-btn secondary">
                      <i className="bi bi-calendar-plus"></i>
                      <span>Request Leave</span>
                    </button>
                    <button className="action-btn tertiary">
                      <i className="bi bi-download"></i>
                      <span>Export Report</span>
                    </button>
                  </div>

                  <div className="stats-row">
                    <div className="stat-card hours-card">
                      <div className="stat-content">
                        <div className="stat-value">
                          78.5<span className="stat-unit">/80h</span>
                        </div>
                        <div className="stat-label">Weekly Hours</div>
                        <div className="d-flex flex-row justify-content-between w-100">
                          <div className="progress-bar">
                            <div
                              className="progress-fill W-75"
                              style={{ width: "98%" }}
                            ></div>
                          </div>
                          <div>
                            <span className="progress-text mt-1">98%</span>{" "}
                          </div>
                        </div>
                        <div className="leave-types mt-2">
                          <span className="leave-type">
                            No work hour violations
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card leave-card">
                      <div className="stat-content">
                        <div className="stat-value">
                          12<span className="stat-unit"> days</span>
                        </div>
                        <div className="stat-label">Vacation Balance</div>
                        <div className="leave-types">
                          <span className="leave-type">Sick: 5d</span>
                          <span className="leave-type">Wellness: 2d</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  
                  <div className="schedule-section">
                    <div className="section-header-col">
                      <h4>This Week's Schedule</h4>
                      <span className="week-indicator">Week 12, 2024</span>
                    </div>
                    <div className="schedule-grid">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                        (day, index) => (
                          <div
                            key={day}
                            className={`schedule-day ${index < 5 ? "work-day" : "weekend"}`}
                          >
                            <div className="day-label">{day}</div>
                            <div className="day-status">
                              {index < 5 ? (
                                <div className="work-indicator">
                                  <i className="bi bi-check-circle-fill"></i>
                                  <span>8h</span>
                                </div>
                              ) : (
                                <div className="weekend-indicator">
                                  <i className="bi bi-circle"></i>
                                  <span>Off</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>

                  
                  <div className="chart-section">
                    <div className="section-header-col">
                      <h4>Hours Trend (30 Days)</h4>
                      <div className="chart-controls">
                        <span className="chart-period active">30D</span>
                        <span className="chart-period">90D</span>
                        <span className="chart-period">1Y</span>
                      </div>
                    </div>
                    <div className="chart-container">
                      <div className="chart-legend">
                        <div className="legend-item">
                          <div
                            className="legend-color"
                            style={{ background: "#3b82f6" }}
                          ></div>
                          <span>Daily Hours</span>
                        </div>
                        <div className="legend-item">
                          <div
                            className="legend-color"
                            style={{ background: "#10b981" }}
                          ></div>
                          <span>ACGME Limit (80h)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>*/}
          </div>
              </Tab.Pane>
              <Tab.Pane eventKey="roles" mountOnEnter className="border-2 rounded-4 bg-card-srf-bg">
          <div className="row">
            {/* Role-Based Access Control */}
            <div className="col-lg-6 order-md-2 order-2">
              <div className="feature-image">
                <div className="hierarchy-chart-container">
                              
                  {/* Team Hierarchy */}
                  <section id="team-hierarchy">
                    <div className="position-relative">
                      <svg
                        ref={hierarchyChartRef}
                        className="hierarchy-chart"
                        viewBox="100 180 620 460"
                        width={460}
                        height={460}
                        aria-hidden="true"
                      />
                      {activeRole && (
                        <div
                          className={`hierarchy-popup-overlay ${activeRole ? "active" : ""}`}
                          onClick={(e) => {
                            if (e.target === e.currentTarget) closeRolePopup();
                          }}
                        >
                          <div className="hierarchy-popup" role="dialog" aria-modal="true">
                            <button
                              type="button"
                              className="hierarchy-popup-close"
                              aria-label="Close role details"
                              onClick={closeRolePopup}
                            >
                              <i className="bi bi-x-lg" aria-hidden="true"></i>
                            </button>
                            <div
                              className="hierarchy-popup-icon"
                              style={{ background: activeRole.color || "#6366f1" }}
                            >
                              <i className={`bi ${activeRole.icon}`} aria-hidden="true"></i>
                            </div>
                            <h4>{activeRole.title}</h4>
                            <p>{activeRole.description}</p>
                          </div>
                        </div>
                      )}
                      {/*<div className="hierarchy-legend">
                        <div className="hierarchy-legend-item">
                          <div className="hierarchy-legend-dot" style={{ background: "#1e1b4b", boxShadow: "0 0 6px #1e1b4b88" }} />
                          Admin
                        </div>
                        <div className="hierarchy-legend-item">
                          <div className="hierarchy-legend-dot" style={{ background: "#3b6fd4", boxShadow: "0 0 6px #3b6fd488" }} />
                          Program Director
                        </div>
                        <div className="hierarchy-legend-item">
                          <div className="hierarchy-legend-dot" style={{ background: "#0f9e8a", boxShadow: "0 0 6px #0f9e8a88" }} />
                          Attending
                        </div>
                        <div className="hierarchy-legend-item">
                          <div className="hierarchy-legend-dot" style={{ background: "#8b5cf6", boxShadow: "0 0 6px #8b5cf688" }} />
                          Residents
                        </div>
                      </div>*/}
                    </div>
                  </section>
                </div>
                {/*<div className="rbac-dashboard-container bg-light-card">
                  <div className="rbac-header">
                    <div className="rbac-title">
                      <i className="bi bi-shield-check"></i>
                      <span>Role-Based Access Control</span>
                    </div>
                    <div className="rbac-subtitle">
                      Secure user management system
                    </div>
                  </div>
                      
                  <div className="roles-grid">
                    <div className="role-card admin">
                      
                    </div>
                    <div className="role-card director">
                      <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                        <div className="role-icon me-3">
                          <i className="bi bi-person-badge"></i>
                        </div>
                        <div className="role-content">
                          <h5>Program Directors</h5>
                          <p>Complete program oversight and management</p>
                        </div>
                      </div>
                      <div className="role-content">
                        <div className="role-permissions">
                          <span className="permission-tag">Scheduling</span>
                          <span className="permission-tag">Compliance</span>
                          <span className="permission-tag">Reports</span>
                        </div>
                      </div>
                    </div>

                    <div className="role-card resident">
                      <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                        <div className="role-icon me-3">
                          <i className="bi bi-person-workspace"></i>
                        </div>
                        <div className="role-content">
                          <h5>Residents</h5>
                          <p>Personal dashboard and progress tracking</p>
                        </div>
                      </div>
                      <div className="role-content">
                        <div className="role-permissions">
                          <span className="permission-tag">Work Hours</span>
                          <span className="permission-tag">Case Logs</span>
                          <span className="permission-tag">Evaluations</span>
                        </div>
                      </div>
                    </div>

                    <div className="role-card attending">
                      <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                        <div className="role-icon me-3">
                          <i className="bi bi-person-video"></i>
                        </div>
                        <div className="role-content">
                          <h5>Attending Physicians</h5>
                          <p>Evaluation and feedback management</p>
                        </div>
                      </div>
                      <div className="role-content">
                        <div className="role-permissions">
                          <span className="permission-tag">Feedback</span>
                          <span className="permission-tag">Evaluations</span>
                          <span className="permission-tag">Mentoring</span>
                        </div>
                      </div>
                    </div>

                    {/*<div className="role-card admin">
                <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                  <div className="role-icon me-3">
                    <i className="bi bi-person-gear"></i>
                  </div>
                  <div className="role-content">
                    <h5>System Administrators</h5>
                    <p>Full system configuration and user management</p>
                  </div>

                </div>
                <div className="role-content">
                  <div className="role-permissions">
                    <span className="permission-tag">User Mgmt</span>
                    <span className="permission-tag">Settings</span>
                    <span className="permission-tag">Security</span>
                  </div>
                </div>
              </div>
                  </div>
                </div>*/}
              </div>
            </div>
            <div className="col-lg-6 order-md-1 order-1">
              <div className="feature-content p-md-5 p-3">
                <div className="d-flex flex-column gap-3">
                  <div className="feature-icon bg-cyan-solid">
                    <i className="bi bi-people"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Role-Based Access Control</h5>
                    <p className="text-muted mb-3">
                      Secure role-based access system providing appropriate
                      functionality for each user type in each program.
                      program.
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-0">
                  Our platform offers director scheduling and evaluation
                  management, a resident portal for tracking hours, cases, and
                  evaluations, and an attending interface for streamlined
                  evaluations and feedback. With granular permission controls,
                  it ensures secure, role-appropriate access, while delivering a
                  seamless user experience that enhances efficiency across the
                  program.
                  </p>
                </div>
              </div>
            </div>
          </div>
              </Tab.Pane>
              <Tab.Pane eventKey="caselog" mountOnEnter>
          <div className="row d-flex align-items-center justify-content-center mb-4">
            {/* Case Log Management */}
            <div className="col-lg-6 order-md-1 mb-5">
              <div className="feature-content">
                <div className="d-flex flex-column gap-3">
                  <div className="feature-icon case">
                    <i className="bi bi-file-medical"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Case Log Management</h5>
                    <p className="text-muted mb-3">
                      Comprehensive case logging with procedure tracking,
                      automatic feedback generation, and detailed analytics
                      dashboard for resident development.
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-0">
                  Our platform streamlines case logging with structured
                  procedure and encounter entry, auto-generated evaluations, and
                  anonymized identifiers to protect patient privacy. A robust
                  analytics dashboard with live statistics, customizable
                  reports, and CSV/printable exports ensures accurate
                  documentation, timely feedback, real-time insight into gaps
                  and trends, and simplified accreditation reporting.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-md-2 mb-5">
              <div className="feature-image">
                <div className="case-log-dashboard-container bg-light-card">
                  {/* Quick Actions */}
                  <div className="quick-actions">
                    <button className="action-btn primary">
                      <i className="bi bi-plus-circle"></i>
                      <span>Log Case</span>
                    </button>
                    <button className="action-btn secondary">
                      <i className="bi bi-file-earmark-text"></i>
                      <span>Generate Report</span>
                    </button>
                    <button className="action-btn tertiary">
                      <i className="bi bi-download"></i>
                      <span>Export Data</span>
                    </button>
                  </div>

                  {/* Quick Stats */}
                  <div className="stats-overview">
                    <div className="stat-item">
                      <div className="stat-card-icon">
                        <i className="bi bi-clipboard-data"></i>
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">123</div>
                        <div className="stat-label">Total Cases</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-card-icon">
                        <i className="bi bi-graph-up-arrow"></i>
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">+12</div>
                        <div className="stat-label">This Month</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-card-icon">
                        <i className="bi bi-check-circle"></i>
                      </div>
                      <div className="stat-content">
                        <div className="stat-number">98%</div>
                        <div className="stat-label">Complete</div>
                      </div>
                    </div>
                  </div>

                  {/* Procedure Categories */}
                  <div className="procedure-categories">
                    <div className="section-header-col">
                      <h4>Procedure Categories</h4>
                      <span className="view-all">View All</span>
                    </div>
                    <div className="categories-grid">
                      <div className="category-card cardiology">
                        <div className="category-header">
                          <div className="category-icon">
                            <i className="bi bi-heart-pulse"></i>
                          </div>
                          <div className="category-info">
                            <h5>Cardiology</h5>
                            <p>45 procedures</p>
                          </div>
                          <div className="category-details">
                            <div className="detail-item">
                              <span className="detail-label">Target:</span>
                              <span className="detail-value">50</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Remaining:</span>
                              <span className="detail-value">5</span>
                            </div>
                          </div>
                          <div className="category-progress">
                            <div className="progress-circle">
                              <svg
                                viewBox="0 0 36 36"
                                className="circular-chart"
                              >
                                <path
                                  className="circle-bg"
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                                <path
                                  className="circle"
                                  strokeDasharray="90, 100"
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                              </svg>
                              <span className="progress-text">90%</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="category-card surgery">
                        <div className="category-header">
                          <div className="category-icon">
                            <i className="bi bi-scissors"></i>
                          </div>
                          <div className="category-info">
                            <h5>Surgery</h5>
                            <p>32 procedures</p>
                          </div>
                          <div className="category-details">
                            <div className="detail-item">
                              <span className="detail-label">Target:</span>
                              <span className="detail-value">50</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Remaining:</span>
                              <span className="detail-value">18</span>
                            </div>
                          </div>
                          <div className="category-progress">
                            <div className="progress-circle">
                              <svg
                                viewBox="0 0 36 36"
                                className="circular-chart"
                              >
                                <path
                                  className="circle-bg"
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                                <path
                                  className="circle"
                                  strokeDasharray="64, 100"
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                              </svg>
                              <span className="progress-text">64%</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="category-card icu">
                        <div className="category-header">
                          <div className="category-icon">
                            <i className="bi bi-hospital"></i>
                          </div>
                          <div className="category-info">
                            <h5>ICU</h5>
                            <p>28 procedures</p>
                          </div>
                          <div className="category-details">
                            <div className="detail-item">
                              <span className="detail-label">Target:</span>
                              <span className="detail-value">50</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Remaining:</span>
                              <span className="detail-value">22</span>
                            </div>
                          </div>
                          <div className="category-progress">
                            <div className="progress-circle">
                              <svg
                                viewBox="0 0 36 36"
                                className="circular-chart"
                              >
                                <path
                                  className="circle-bg"
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                                <path
                                  className="circle"
                                  strokeDasharray="56, 100"
                                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                />
                              </svg>
                              <span className="progress-text">56%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  {/* <div className="recent-activity">
              <div className="section-header-col">
                <h4>Recent Activity</h4>
                <span className="activity-count">12 items</span>
              </div>
              <div className="activity-list">
                <div className="activity-item">
                  <div className="activity-icon completed">
                    <i className="bi bi-check-circle-fill"></i>
                  </div>
                  <div className="activity-content">
                    <div className="activity-title">Cardiac Catheterization</div>
                    <div className="activity-meta">Dr. Smith • 2 hours ago</div>
                  </div>
                  <div className="activity-status">
                    <span className="status-badge completed">Completed</span>
                  </div>
                </div>
                
                <div className="activity-item">
                  <div className="activity-icon pending">
                    <i className="bi bi-clock"></i>
                  </div>
                  <div className="activity-content">
                    <div className="activity-title">Appendectomy</div>
                    <div className="activity-meta">Dr. Johnson • 4 hours ago</div>
                  </div>
                  <div className="activity-status">
                    <span className="status-badge pending">Pending Review</span>
                  </div>
                </div>
                
                <div className="activity-item">
                  <div className="activity-icon completed">
                    <i className="bi bi-check-circle-fill"></i>
                  </div>
                  <div className="activity-content">
                    <div className="activity-title">ICU Intubation</div>
                    <div className="activity-meta">Dr. Williams • 6 hours ago</div>
                  </div>
                  <div className="activity-status">
                    <span className="status-badge completed">Completed</span>
                  </div>
                </div>
              </div> 
            </div>*/}
                </div>
              </div>
            </div>
          </div>
              </Tab.Pane>
              <Tab.Pane eventKey="notifications" mountOnEnter className="border-2 rounded-4 p-md-5 p-3 bg-card-srf-bg">
          <div className="row">
            {/* Smart Notification & Announcement System */}
            <div className="col-lg-6 order-2 d-flex align-items-end justify-content-end">
              <div
                className="mockup-widgets-preview w-100 h-100"
                style={{ maxWidth: "500px"}}
              >
                <div className="conf-tile">
                  <div className="widget-header">Active Alerts</div>
                  <div className="alert-list">
                    {notificationData
                      .slice(
                        Math.max(0, notificationIndex - 2),
                        notificationIndex + 1,
                      )
                      .map((notification, index) => (
                        <div
                          key={index}
                          className={`alert-item ${notification.type} animated-notification mb-2`}
                        >
                          <div className="notification-icon">
                            <i className={`bi ${notification.icon}`}></i>
                          </div>
                          <div className="notification-content">
                            <div className="notification-title">
                              {notification.title}
                            </div>
                            <div className="notification-time">
                              {notification.time}
                            </div>
                          </div>
                          {notification.badge && (
                            <div className="notification-badge">
                              {notification.badge}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
                <div className=" conf-tile">
                  <div className="widget-header">Program Announcements</div>
                  <div className="widget-content">
                    <div className="announcement-list">
                      <div className="announcement-item animated-announcement">
                        <div className="announcement-icon">
                          <i className="bi bi-megaphone"></i>
                        </div>
                        <div className="announcement-content">
                          <div className="announcement-title">
                            {announcementData[announcementIndex].title}
                          </div>
                          <div className="announcement-description">
                            {announcementData[announcementIndex].description}
                          </div>
                          <div className="announcement-time">
                            {announcementData[announcementIndex].time}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-1">
              <div>
                <div className="d-flex flex-column gap-3">
                  <div className="feature-icon bg-orange-solid">
                    <i className="bi bi-bell"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Notification & Announcement System</h5>
                    <p className="text-muted mb-3">
                      Notification system with real-time alerts, targeted
                      announcements, and comprehensive communication management.
                    </p>
                  </div>
                </div>
                <div>
                  <p className="mb-0">
                  Our platform enhances program communication and compliance
                  with real-time evaluation assignments, reminder notifications,
                  program-wide announcements, work log submission alerts, event
                  invitations with response tracking, and role-specific
                  notification filtering and management. These features improve
                  evaluation completion rates, ensure stakeholders receive
                  critical updates, reduce missed deadlines and compliance
                  issues, and foster seamless communication across the entire
                  program.
                  </p>
                </div>
              </div>
            </div>
          </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
          <br />
          <br />
          <Row className="py-md-5 py-3 g-4">
            <Col md={12}>
              <h2 className="fw-bold">
                Bring your{" "}
                <span className="hero-title-highlight">
                  conferences, resources, and learning discussions
                </span>{" "}
                into one modern workspace.
              </h2>
            </Col>
            {/* Conference attendance column */}
            <Col md={6} className="mb-5">
              <div className="feature-pill feature-pill-conference h-100">
                <div className="d-flex flex-column p-md-4 p-3">
                <div className="feature-pill-header">
                  <h5 className="mb-1 fw-bold">Conference Attendance</h5>
                  <p className="text-muted">
                    Track didactics, grand rounds, and specialty conferences in
                    real time with QR-based sign‑in and program‑ready reports.
                  </p>
                </div>
                <ul className="featured-items-list list-unstyled">
                  <li>One‑tap QR sign‑in from any device</li>
                  <li>Central document library for conference related materials, slide decks, recordings, and key files</li>
                  <li>Attendance tracking and report generation for compliance and CME credit </li>
                </ul>
                </div>
                <div className="feature-pill-visual">
                  <img
                    src={isDarkMode ? conferenceMockupDark : conferenceMockup}
                    alt="Conference attendance mockup"
                    className="conference-img"
                  />
                </div>
              </div>
            </Col>

            {/* Education column */}
            <Col md={6} className="mb-5">
              <Row className="h-100">
                {/* Resources column */}
                <Col md={12} className="mb-3">
                  <div className="feature-pill feature-pill-resources h-100">
                    <div className="d-flex flex-column p-md-4 p-3">
                      <div className="feature-pill-header">
                        <h5 className="mb-1 fw-bold">Learning Resources</h5>
                        <p className="text-muted">
                          Give residents and faculty a single organized home for
                          sharing and accessing educational resources such as policies, templates, and learning materials.
                        </p>
                      </div>
                      <ul className="featured-items-list list-unstyled">
                        <li>Create a library for residents to upload and share learning documents and other materials within your program</li>
                        <li>Select and pin important and high‑yield items in the sidebar sections for quick access</li>
                        <li>Use keywords and tags to categorize content by topic, rotation, or other relevant criteria, enabling residents to quickly locate the information they need.</li>
                      </ul>
                    </div>
                  </div>
                </Col>

                {/* Discussions column */}
                <Col md={12}>
                  <div className="feature-pill feature-pill-discussions h-100">
                    <div className="d-flex flex-column p-md-4 p-3">
                      <div className="feature-pill-header">
                        <h5 className="mb-1 fw-bold">Discussion Spaces</h5>
                        <p className="text-muted">
                          Capture questions, pearls, and follow‑ups in dedicated
                          spaces that mirror how your teams actually learn.
                        </p>
                      </div>
                      <ul className="featured-items-list list-unstyled">
                        <li>Create topic categories for specific learning needs to streamline discussions (e.g. Grand Rounds, Case of the Week, journal clubs, etc.)</li>
                        <li>User generated discussion threads with selected audience controls (all, residents, attendings, etc.)</li>
                        <li>Searchable archive of past discussions and teaching pearls with trending hashtags and filters to find active or past topics</li>
                        <li>Threaded comments and replies to capture teaching points</li>
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </section>

      <br />
      {/* Show More Features CTA */}
      <section className="more-features">
        <div className="container text-center">
          <div className="section-header text-center mb-3">
            <div className="d-flex flex-column justify-content-center align-items-center">
              <h2 className="fw-bold mb-3">There's more to <span className="hero-title-highlight">Medhive</span> than you think</h2>
              <p className="text-muted ">
                Explore additional features  Medhive has to offer and look ahead to what's coming next.
              </p>
            </div>
          </div>
          <button
            className={`btn  ${showMoreFeatures ? "btn-primary" : "btn-outline-primary"} rounded-pill px-4 py-3 `}
            onClick={() => setShowMoreFeatures((prev) => !prev)}
          >
            {showMoreFeatures ? (
              <>
                <i className="bi bi-chevron-up me-2"></i>
                Show less
              </>
            ) : (
              <>
                <i className="bi bi-grid-3x3-gap me-2"></i>
                Show more features
              </>
            )}
          </button>

          {showMoreFeatures &&
            (() => {
              const availableFeatures = moreFeatures.filter(
                (f) => f.badge !== "Coming soon",
              );
              const comingSoonFeatures = moreFeatures.filter(
                (f) => f.badge === "Coming soon",
              );
              return (
                <div className="text-start mt-4">
                  {availableFeatures.length > 0 && (
                    <>
                      <h5 className="mb-3 fw-bold">Additional Features</h5>
                      <div className="more-features-grid mb-2">
                        {availableFeatures.map((feat, idx) => (
                          <div
                            key={`avail-${idx}`}
                            className="feature-card-compact"
                          >
                            <div className="feature-card-icon">
                              <i className={`bi ${feat.icon}`}></i>
                            </div>
                            <div className="feature-card-body">
                              <div className="feature-card-title d-flex align-items-center gap-2">
                                <span>{feat.title}</span>
                                {feat.badge && (
                                  <span
                                    className={`badge ${feat.badge === "Coming soon" ? "badge-coming" : "badge-new"}`}
                                  >
                                    {feat.badge}
                                  </span>
                                )}
                              </div>
                              <div className="feature-card-desc">
                                {feat.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {comingSoonFeatures.length > 0 && (
                    <>
                      <h5 className="mb-3 fw-bold">Coming Soon</h5>
                      <div className="more-features-grid">
                        {comingSoonFeatures.map((feat, idx) => (
                          <div
                            key={`soon-${idx}`}
                            className="feature-card-compact coming-soon"
                          >
                            <div className="feature-card-icon">
                              <i className={`bi ${feat.icon}`}></i>
                            </div>
                            <div className="feature-card-body">
                              <div className="feature-card-title d-flex align-items-center gap-2">
                                <span>{feat.title}</span>
                                {feat.badge && (
                                  <span
                                    className={`badge ${feat.badge === "Coming soon" ? "badge-coming" : "badge-new"}`}
                                  >
                                    {feat.badge}
                                  </span>
                                )}
                              </div>
                              <div className="feature-card-desc">
                                {feat.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
        </div>
      </section>

      {/* How it works — sequential highlight repeats through all steps */}
      <section
        className={`how-it-works-section ${howItWorksMotionOk ? "how-it-works-section--animated" : "how-it-works-section--reduced-motion"}`}
        aria-labelledby="how-it-works-heading"
      >
        <div className="container">
          <div className="section-header text-center mb-md-4 mb-3">
            <h2 id="how-it-works-heading" className="fw-bold mb-3">
              How <span className="hero-title-highlight">it works</span>
            </h2>
            <p className="text-muted mx-auto how-it-works-lead mb-0">
              Set up your program, assign schedules, then track outcomes—Medhive walks
              you through each stage in order.
            </p>
          </div>
          <div className="mb-md-5 mb-4 px-1 px-sm-2">
            <div
              className="how-works-progress how-works-progress--steps"
              role="radiogroup"
              aria-label="Workflow demo steps — select any pill"
              aria-orientation="horizontal"
              style={{
                gridTemplateColumns: `repeat(${HOW_IT_WORKS_FULL_SEQUENCE_LEN}, minmax(0, 1fr))`,
              }}
            >
              {Array.from(
                { length: HOW_IT_WORKS_FULL_SEQUENCE_LEN },
                (_, i) => {
                  const segmentFilled = i <= howItWorksSeq;
                  const segmentLatest =
                    howItWorksMotionOk &&
                    segmentFilled &&
                    i === howItWorksSeq;
                  let stepLabel;
                  if (i < HOW_IT_WORKS_SOLO_TICKS) {
                    stepLabel =
                      i === 0 ? "Creating account" : "Account ready";
                  } else if (i >= HOW_IT_WORKS_SUCCESS_START_SEQ) {
                    stepLabel =
                      i === HOW_IT_WORKS_SUCCESS_START_SEQ
                        ? "Success highlight"
                        : "Peace of mind";
                  } else {
                    stepLabel = `Workflow step ${
                      i - HOW_IT_WORKS_GRID_START_SEQ + 1
                    }`;
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`how-works-progress-slot ${howItWorksSeq === i ? "how-works-progress-slot--current" : ""}`}
                      onClick={() => goHowItWorksStep(i)}
                      onKeyDown={(e) => onHowWorksSegmentKeyDown(e, i)}
                      aria-label={`Step ${i + 1} of ${HOW_IT_WORKS_FULL_SEQUENCE_LEN}: ${stepLabel}`}
                      aria-current={howItWorksSeq === i ? "step" : undefined}
                    >
                      <span
                        aria-hidden="true"
                        className={`how-works-progress-slot-fill ${
                          segmentFilled ? "how-works-progress-slot-fill--full" : ""
                        } ${segmentLatest ? "how-works-progress-slot-fill--latest" : ""}`}
                      />
                    </button>
                  );
                },
              )}
            </div>
            <p className="text-muted small text-center mt-3 mb-0">
              Tap a pill to navigate the demo.&nbsp;
              {howItWorksMotionOk ? (
                howItWorksAutoplayPaused ? (
                  <button
                    type="button"
                    className="how-works-resume-demo-btn btn btn-link btn-sm align-baseline link-primary text-decoration-underline p-0"
                    onClick={resumeHowItWorksAutoplay}
                  >
                    Resume autoplay
                  </button>
                ) : (
                  <span aria-hidden="true">Autoplay is on.</span>
                )
              ) : (
                <>
                  Reduced motion skips autoplay; tab to each pill and use arrows to jump
                  between steps.
                </>
              )}
            </p>
          </div>
          {howItWorksShowSoloStage && (
            <div className="how-account-solo-stage mb-4 mb-md-5" aria-live="polite">
              <div
                className={`how-account-card mx-auto ${
                  howItWorksSeq === 0
                    ? "how-account-card--running"
                    : "how-account-card--complete"
                }`}
              >
                <div className="how-account-card-icon mx-auto mb-3" aria-hidden="true">
                  {howItWorksSeq === 0 ? (
                    <i className="bi bi-person-plus-fill"></i>
                  ) : (
                    <i className="bi bi-check-circle-fill how-account-done-icon"></i>
                  )}
                </div>
                <h5 className="fw-bold text-center mb-2">
                  {HOW_IT_WORKS_ACCOUNT_CARD_TITLE}
                </h5>
                <p className="text-muted small text-center mb-3 how-account-caption">
                  {howItWorksSeq === 0
                    ? "We’re creating your workspace and verifying your institution."
                    : "You’re signed in — next, configure program details and rotations."}
                </p>
                {howItWorksSeq === 0 ? (
                  <div className="how-account-ring-wrap mx-auto mb-3" aria-hidden="true">
                    <div className="how-account-ring"></div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center align-items-center gap-2 how-account-complete-row">
                    <span className="how-account-complete-badge rounded-pill">
                      <i className="bi bi-check2-circle me-1" aria-hidden="true"></i>
                      Complete
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {howItWorksShowGrid && (
          <Row
            className={`g-4 justify-content-center how-it-works-steps align-items-stretch ${
              howItWorksMotionOk &&
              howItWorksSeq >= HOW_IT_WORKS_GRID_START_SEQ &&
              howItWorksSeq < HOW_IT_WORKS_SUCCESS_START_SEQ
                ? "how-works-grid-reveal"
                : ""
            }`}
          >
            <Col md={4}>
              <div
                className={`how-step-card h-100 how-step-card--rail ${
                  howItWorksSetupPhaseActive ? "how-step-card--phase-active" : ""
                }`}
              >
                <div className="how-step-index" aria-hidden="true">
                  1
                </div>
                <div className="how-step-icon how-step-icon--sm mx-auto mb-3">
                  <i className="bi bi-folder-plus"></i>
                </div>
                <h5
                  className={`fw-bold mb-3 text-center ${
                    howItWorksSetupPhaseActive ? "how-seq-item-parent--active" : ""
                  }`}
                >
                  Set up your program
                </h5>
                <p className="text-muted small text-center mb-3 how-phase-intro">
                  Build the foundations your team needs before the academic year ramps
                  up.
                </p>
                <ul
                  className="how-seq-list list-unstyled mb-0"
                  aria-label="Program setup checklist"
                >
                  {HOW_IT_WORKS_SETUP_ITEMS.map((label, idx) => (
                    <li
                      key={label}
                      className={`how-seq-item ${howItWorksGridIx === idx ? "how-seq-item--active" : ""}`}
                    >
                      <span className="how-seq-bullet" aria-hidden="true"></span>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
            <Col md={4}>
              <div
                className={`how-step-card h-100 how-step-card--rail ${
                  howItWorksAssignPhaseActive ? "how-step-card--phase-active" : ""
                }`}
              >
                <div className="how-step-index" aria-hidden="true">
                  2
                </div>
                <div className="how-step-icon how-step-icon--sm mx-auto mb-3">
                  <i className="bi bi-calendar2-check"></i>
                </div>
                <h5
                  className={`fw-bold mb-3 text-center ${
                    howItWorksAssignPhaseActive ? "how-seq-item-parent--active" : ""
                  }`}
                >
                  Assign &amp; schedule
                </h5>
                <p className="text-muted small text-center mb-3 how-phase-intro">
                  Turn configured rotations into live schedules your whole program can
                  trust.
                </p>
                <ul
                  className="how-seq-list list-unstyled mb-0"
                  aria-label="Assign and schedule steps"
                >
                  {HOW_IT_WORKS_ASSIGN_ITEMS.map((label, idx) => {
                    const seqIx = HOW_IT_WORKS_ASSIGN_START + idx;
                    return (
                      <li
                        key={label}
                        className={`how-seq-item ${howItWorksGridIx === seqIx ? "how-seq-item--active" : ""}`}
                      >
                        <span className="how-seq-bullet" aria-hidden="true"></span>
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Col>
            <Col md={4}>
              <div
                className={`how-step-card h-100 how-step-card--rail ${
                  howItWorksTrackPhaseActive ? "how-step-card--phase-active" : ""
                }`}
              >
                <div className="how-step-index" aria-hidden="true">
                  3
                </div>
                <div className="how-step-icon how-step-icon--sm mx-auto mb-3">
                  <i className="bi bi-activity"></i>
                </div>
                <h5
                  className={`fw-bold mb-3 text-center ${
                    howItWorksTrackPhaseActive ? "how-seq-item-parent--active" : ""
                  }`}
                >
                  Track &amp; manage
                </h5>
                <p className="text-muted small text-center mb-3 how-phase-intro">
                  Stay audit-ready while residents and faculty work from the same live
                  data.
                </p>
                <ul
                  className="how-seq-list list-unstyled mb-0"
                  aria-label="Track and manage steps"
                >
                  {HOW_IT_WORKS_TRACK_ITEMS.map((label, idx) => {
                    const seqIx = HOW_IT_WORKS_TRACK_START + idx;
                    return (
                      <li
                        key={label}
                        className={`how-seq-item ${howItWorksGridIx === seqIx ? "how-seq-item--active" : ""}`}
                      >
                        <span className="how-seq-bullet" aria-hidden="true"></span>
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Col>
          </Row>
          )}
          {howItWorksShowClosingStage && (
            <div
              className="how-success-solo-stage mb-4 mb-md-5 how-success-solo-reveal"
              aria-live="polite"
            >
              <div
                className={`how-success-card mx-auto ${
                  howItWorksSeq === HOW_IT_WORKS_SUCCESS_START_SEQ
                    ? "how-success-card--arrive"
                    : "how-success-card--radiant"
                }`}
              >
                <div className="how-success-card-icon mx-auto mb-3" aria-hidden="true">
                  {howItWorksSeq === HOW_IT_WORKS_SUCCESS_START_SEQ ? (
                    <i className="bi bi-patch-check-fill how-success-phase-icon"></i>
                  ) : (
                    <i className="bi bi-shield-fill-check how-success-phase-icon"></i>
                  )}
                </div>
                <h5 className="fw-bold text-center mb-2 text-wrap">
                  <span className="hero-title-highlight">
                    {HOW_IT_WORKS_CLOSING_TITLE}
                  </span>
                </h5>
                <p className="text-muted small text-center mb-3 how-success-caption">
                  {
                    HOW_IT_WORKS_CLOSING_CAPTIONS[
                      howItWorksSeq - HOW_IT_WORKS_SUCCESS_START_SEQ
                    ]
                  }
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-2 how-success-chip-row">
                  <span className="how-success-chip rounded-pill">Program clarity</span>
                  <span className="how-success-chip rounded-pill">Peace of mind</span>
                  {howItWorksSeq > HOW_IT_WORKS_SUCCESS_START_SEQ && (
                    <span className="how-success-chip how-success-chip--accent rounded-pill">
                      Ready for survey season
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <div className="container">
          <div className="row g-4">
            <div className="col-md-3 col-6">
              <div className="stat-card">
                <div className="stat-icon stat-icon-bg-primary">
                  <i className="bi bi-people-fill"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number" data-count="">
                    0
                  </h3>
                  <p className="stat-label">Active Users</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="stat-card">
                <div className="stat-icon stat-icon-bg-teal">
                  <i className="bi bi-hospital"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number" data-count="">
                    0
                  </h3>
                  <p className="stat-label">Institutions</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="stat-card">
                <div className="stat-icon stat-icon-bg-purple">
                  <i className="bi bi-building"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number" data-count="">
                    0
                  </h3>
                  <p className="stat-label">GME Programs</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="stat-card">
                <div className="stat-icon stat-icon-bg-orange">
                  <i className="bi bi-star-fill"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-number" data-count="">
                    0
                  </h3>
                  <p className="stat-label">Satisfaction Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="cta-early-access shadow-sm text-center">
            {/* decorative orbs */}
            <div className="orb-left"></div>
            <div className="orb-left-2"></div>
            <div className="orb-left-3"></div>
            <div className="orb-right"></div>
            <div className="section-header mb-3">
              <div className="d-flex flex-column justify-content-center align-items-center">
                <span className="cta-badge">Early Access</span>
                <h3 className="mt-2 fw-bold">
                  Be the first to try our platform — free
                </h3>
                <div className="text-hl-home"></div>
              </div>
              <p className="text-muted mt-2">
                Join our early access program to explore rotation scheduling,
                evaluations, work hours, conferences, and onboarding—all in one
                place. We'll help you get set up, and tailor workflows to your
                program's needs.
              </p>
              <p className="text-muted">
                Contact us for more information and early access.
              </p>
            </div>
            <div className="d-flex flex-column flex-md-row justify-content-center gap-2">
              <Link to="/contact" className="main-btn main-btn-sm main-primary-btn">
                Get Early Access
              </Link>
              <a
                href="#demo"
                className="main-btn main-btn-sm main-secondary-btn"
                onClick={(e) => {
                  e.preventDefault();
                  const demoSection = document.getElementById("demo");
                  if (demoSection) {
                    const navbarHeight = 80; // Approximate height of fixed navbar
                    const elementPosition =
                      demoSection.getBoundingClientRect().top;
                    const offsetPosition =
                      elementPosition + window.pageYOffset - navbarHeight;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="mb-5">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span className="cta-badge mb-2">Pricing</span>
              <h2 className="fw-bold mb-2">Pricing that tailors to your programs size and structure</h2>
            <p className="text-muted">
              Every plan includes all features, and support. Choose the plan that matches your program's size and structure—no hidden fees, no feature restrictions.
            </p> 
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="pricing-card d-flex flex-column justify-content-center">
                <div className="pricing-header">
                  <h6 className="fw-bold mb-0 text-muted">Small Programs</h6>
                  <div className="price">
                    <span className="currency">$</span>
                    <span className="amount">200</span>
                    <span className="period">/month</span>
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                <ul className="pricing-features">
                  <li>
                    <i className="bi bi-check-circle-fill"></i>All platform
                    features included
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i> up to 30
                    resident accounts
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i> up to 10 faculty
                    accounts
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Ideal for
                    single-site programs
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Email support
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Guided onboarding
                  </li>
                </ul>
                </div>
                <div className="d-flex justify-content-center">
                  <Link to="/signup" className="main-btn main-btn-sm main-secondary-btn">
                    Get Started
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="pricing-card d-flex flex-column justify-content-center popular">
                <div className="popular-badge">Most Popular</div>
                <div className="pricing-header">
                  <h6 className="fw-bold mb-0 text-muted">Medium Programs</h6>
                  <div className="price">
                    <span className="currency">$</span>
                    <span className="amount">300</span>
                    <span className="period">/month</span>
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                <ul className="pricing-features">
                  <li>
                    <i className="bi bi-check-circle-fill"></i>All platform
                    features included
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>up to 50 resident
                    accounts
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>up to 30 faculty
                    accounts
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Email & chat
                    support
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Personalized
                    onboarding session
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Performance
                    analytics & reporting
                  </li>
                </ul>
                </div>
                <div className="d-flex justify-content-center">
                  <Link to="/signup" className="main-btn main-btn-sm main-primary-btn">
                    Get Started
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="pricing-card d-flex flex-column justify-content-center">
                <div className="pricing-header">
                  <h6 className="fw-bold mb-0 text-muted">Institutions</h6>
                  <div className="price">
                    <span className="amount">Custom</span>
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                <ul className="pricing-features">
                  <li>
                    <i className="bi bi-check-circle-fill"></i>All platform
                    features included
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Unlimited
                    residents, faculty, and programs
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Custom
                    integrations
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Email, chat and
                    phone support
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Dedicated account
                    manager
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill"></i>Tailored
                    onboarding
                  </li>
                </ul>
                </div>
                <div className="d-flex justify-content-center">
                  <Link to="/contact" className="main-btn main-btn-sm main-secondary-btn">
                    Contact Sales
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <p className="text-muted">
              <strong>All plans include:</strong> HIPAA compliant hosting, regular
              updates, limited file storage, and access to all future features.
            </p>
          </div>
        </div>
      </section>

      {/* Request a Demo Section */}
      <section className="demo-section" id="demo">
        <div className="container">
          <div className="row py-5">
            {/* Copy / value props */}
            <div className="col-md-7">
              <span className="cta-badge mb-2">
                <i className="bi bi-record-circle text-success me-1"></i> Live walk‑through
              </span>
              <h2 className="mb-2 fw-bold">
                Schedule a demo with our team
              </h2>
              <p className="text-muted">
                See how MedHive fits your residency program, from work‑hours to evaluations,
                in a live, guided session.
              </p>
     
              <div>
                <ul className="list-unstyled mb-0">
                  <li className="d-flex flex-row align-items-center justify-content-start mb-3">
                    <span className="me-3 text-primary">
                      <i className="bi bi-stars"></i>
                    </span>
                    <p className="text-start mb-0">
                      Tailored overview of the platform based on your{" "}
                      <b>unique program structure</b>.
                    </p>
                  </li>
                  <li className="d-flex flex-row align-items-center justify-content-start mb-3">
                    <span className="me-3 text-success">
                      <i className="bi bi-check2-circle"></i>
                    </span>
                    <p className="text-start mb-0">
                      Clear mapping of how MedHive helps you{" "}
                      <b>solve your top 2–3 pain points</b>.
                    </p>
                  </li>
                  <li className="d-flex flex-row align-items-center justify-content-start mb-3">
                    <span className="me-3 text-info">
                      <i className="bi bi-person-video2"></i>
                    </span>
                    <p className="text-start mb-0">
                      Live tour of key workflows with time for{" "}
                      <b>questions and deep‑dives</b>.
                    </p>
                  </li>
                  <li className="d-flex flex-row align-items-center justify-content-start mb-0">
                    <span className="me-3 text-warning">
                      <i className="bi bi-people"></i>
                    </span>
                    <p className="text-start mb-0">
                      Guidance on rollout, change management, and{" "}
                      <b>support for your team</b>.
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Demo form */}
            <div className="col-md-5 px-md- px-2">
              <form
                className="rounded-4 p-4 py-3 "
                onSubmit={handleDemoSubmit}
              >
                <div className="d-flex flex-column justify-content-start">
                  <p className="text-muted small mb-3">
                    Share a few details and we&apos;ll follow up with times that
                    work for your team.
                  </p>
                </div>
                {demoSuccess && (
                  <div className="alert alert-success mt-2" role="alert">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Thank you! We'll contact you soon to schedule your demo.
                  </div>
                )}
                {demoError && (
                  <div className="alert alert-danger mt-2" role="alert">
                    <i className="bi bi-x-circle-fill me-2"></i>
                    {demoError}
                  </div>
                )}
                <div className="mb-3">
                  <FloatingLabel controlId="demoName" label="Name" className="required-field">
                    <Form.Control
                      type="text"
                      className="form-control"
                      placeholder="Enter your name"
                      required
                      value={demoForm.name}
                      onChange={handleDemoInputChange}
                    />
                  </FloatingLabel>
                </div>
                <div className="mb-3">
                  <FloatingLabel controlId="demoEmail" label="Email"  className="required-field">
                    <Form.Control
                      type="email"
                      className="form-control"
                      placeholder="Enter your work email"
                      required
                      value={demoForm.email}
                      onChange={handleDemoInputChange}
                    />
                  </FloatingLabel>
                </div>
                <div className="row">
                  <div className="mb-3 col-md-6">
                    <FloatingLabel controlId="demoCompany" label="Institution" className="required-field">
                      <Form.Control
                        type="text"
                        className="form-control"
                        placeholder="Enter your company name"
                        required
                        value={demoForm.company}
                        onChange={handleDemoInputChange}
                      />
                    </FloatingLabel>
                  </div>
                  <div className="mb-3 col-md-6">
                    <FloatingLabel controlId="demoDateTime" label="Preferred Date & Time" className="required-field">
                      <Form.Control
                        type="datetime-local"
                        className="form-control"
                        placeholder="Select preferred date and time"
                        required
                        value={demoForm.dateTime}
                        onChange={handleDemoInputChange}
                      />
                    </FloatingLabel>
                  </div>
                </div>
                <div className="mb-3">
                  <FloatingLabel controlId="demoMessage" label="What would you like to focus on?" className="required-field">
                    <Form.Control
                      as="textarea"
                      className="form-control"
                      placeholder="Tell us about your program and goals"
                      required
                      value={demoForm.message}
                      onChange={handleDemoInputChange}
                    />
                  </FloatingLabel>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="main-btn main-btn-sm main-primary-btn"
                    disabled={demoLoading}
                  >
                    {demoLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Sending...
                      </>
                    ) : (
                      "Book a demo"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section position-relative">
        {/*<div className="position-absolute s-circle-blur-2" style={{ bottom: '50%', left: '60%' }}></div>*/}
        {/*<div className="position-absolute s-circle-blur" style={{ top: '70%', left: '50%' }}></div>*/}
        {/*<div className="position-absolute s-circle-blur-2" style={{ top: '70%', right: '40%' }}></div>*/}
        {/*<div className="position-absolute s-circle-blur" style={{ top: '70%', right: '50%' }}></div>*/}
        <div className="container text-center">
          
          <h2 className=" t ext-white fw-bold mb-4">Ready to Get Started?</h2>
          <p className="lead mb-3">
            Start managing your medical education program with comprehensive
            rotation scheduling, evaluations, and compliance tracking
          </p>
          <button className="main-btn main-btn-sm main-dark-btn" onClick={() => {
            navigate("/signup");
          }}>
            Sign Up Now
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
